import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, auth } from '../firebase.config';
import { collection, doc, onSnapshot, query, where, serverTimestamp, getDoc, getDocs } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedUpdateDoc as updateDoc, enforcedDeleteDoc as deleteDoc } from '../utils/enforcedTracking';
import { useLanguage } from '../contexts/LanguageContext';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany } from '../utils/dataAdapter';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { formatDateToCompanyFormat } from '../utils/dateUtils';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { roundTo2Decimals } from '../utils/numberFormat';
import {
  getMonthlySalaryRows,
  calculateAnnualCTC,
  buildSalaryPayload,
  getAnnualBasic,
  getAnnualBonus,
  getAnnualCTC,
  employeeNeedsSalaryMigration,
} from '../utils/salaryUtils';
import { validateSalaryForm, submitSalaryPayment } from '../utils/salaryPaymentSubmit';
import {
  exportSalaryPaymentsCSV,
  exportSalaryPaymentsExcel,
  exportSalaryPaymentsPDF,
} from '../utils/salaryPaymentExports';
import SalarySlip from './Salary/SalarySlip';
import SalaryProcessingTab from './Salary/SalaryProcessingTab';
import EmployeeManagementRedirect from './Salary/EmployeeManagementRedirect';
import LabourAdvancesTab from './Labour/LabourAdvancesTab';
import GroupedExpensesTab from './Expenses/GroupedExpensesTab';
import { getGroupTabAccent } from './Expenses/expenseGroupTheme';
import { SALARY_THEME } from './Salary/salaryTheme';
import { StandardModal, StandardButton, MODAL_SECTIONS } from './Modal';
import { useTableSort } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import { useNotification } from '../contexts/NotificationContext';
import { isEmployeeActive } from '../utils/employeeStatusUtils';
import { EmployeeCashManager } from '../utils/employeeCashManager';
import { ENABLE_WORK_ORDERS } from '../config/featureFlags';
import {
  appendWorkOrderFields,
  syncLedgerFromSource,
  removeLedgerEntry,
  LEDGER_SOURCE_TYPES,
} from '../utils/workOrderLedger';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  CreditCard,
  Pencil,
  Trash2,
  TrendingDown,
  TrendingUp,
  User,
  Wallet,
} from 'lucide-react';
import MobileScrollTabs from './shared/MobileScrollTabs';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const GROUPS = [
  { key: 'fixed', label: 'Fixed' },
  { key: 'variable', label: 'Variable' },
  { key: 'advances', label: 'Labour/Advances' },
  { key: 'salaries', label: 'Salary' },
];

const FIXED_HEADS = ['Rent', 'Electricity', 'Internet', 'Water', 'Other'];
const VARIABLE_HEADS = ['Office Expense', 'Stationery', 'Travel', 'Miscellaneous'];

// Constant entries for each category
const CONSTANT_ENTRIES = {
  'Rent': ['Monthly Rent', 'Office Rent', 'Warehouse Rent', 'Shop Rent'],
  'Electricity': ['Monthly Electricity Bill', 'Office Electricity', 'Factory Electricity', 'Shop Electricity'],
  'Internet': ['Monthly Internet Bill', 'Office Internet', 'Broadband Connection'],
  'Water': ['Monthly Water Bill', 'Office Water', 'Factory Water'],
  'Office Expense': ['Office Supplies', 'Stationery Items', 'Printing & Paper', 'Office Equipment'],
  'Stationery': ['Pens & Pencils', 'Notebooks', 'Files & Folders', 'Stapler & Clips'],
  'Travel': ['Fuel Expenses', 'Taxi Fare', 'Bus/Train Tickets', 'Flight Tickets', 'Hotel Stay'],
  'Miscellaneous': ['Other Expenses', 'Unexpected Costs', 'Emergency Expenses']
};

const storage = getStorage();
const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Employee management has been moved to Employee Management page
// This component now only handles salary processing

const Expenses = ({ db, userId, isAuthReady, appId, setShowSettings }) => {
  // Use enhanced company context for employees
  const { employees: contextEmployees, currentCompany, deleteEmployee } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const companyCtx = useMemo(() => ({ companyId, appId, userId }), [companyId, appId, userId]);
  const { currency: companyCurrency } = useCompanyCurrency();
  const { t } = useLanguage();
  const { paymentSuccess, success, error, warning, info } = useNotification();
  
  // Debug logging for context values
  console.log('🔍 Expenses component context values:', {
    companyId,
    currentCompany,
    isAuthReady,
    userId,
    appId,
    employeesCount: contextEmployees?.length || 0
  });

  // Test function to add sample data
  const addSampleData = async () => {
    if (!companyId || !userId || !appId) {
      console.log('❌ Cannot add sample data - missing required values');
      return;
    }

    try {
      // Add sample irregular payment (advance)
      const irregularData = {
        date: new Date().toISOString().split('T')[0],
        personType: 'Employee',
        employeeId: contextEmployees[0]?.employeeId || 'EMP001',
        personName: contextEmployees[0]?.name || 'Test Employee',
        paymentType: 'Advance',
        amount: 5000,
        remark: 'Test advance payment',
        paymentMode: 'Cash',
        applied: false
      };

      await addDoc(getCollectionForCompany(db, { companyId, appId, userId }, 'irregularPayments'), irregularData);
      console.log('✅ Sample irregular payment added');

      // Add sample salary payment
      const salaryData = {
        employeeId: contextEmployees[0]?.employeeId || 'EMP001',
        employeeName: contextEmployees[0]?.name || 'Test Employee',
        post: contextEmployees[0]?.designation || 'Manager',
        date: new Date().toISOString().split('T')[0],
        month: new Date().toISOString().slice(0, 7),
        fixedRows: [
          { type: 'Basic Salary', amount: '30000', remark: 'Fixed Component', fixed: true, removable: false }
        ],
        performanceRows: [],
        deductions: [],
        irregularApplied: [],
        irregularEarningsTotal: 0,
        irregularDeductionsTotal: 0,
        totalEarnings: 30000,
        totalDeductions: 0,
        netAmount: 30000,
        paymentMode: 'Cash',
        createdAt: serverTimestamp()
      };

      await addDoc(getCollectionForCompany(db, { companyId, appId, userId }, 'salaryPayments'), salaryData);
      console.log('✅ Sample salary payment added');

    } catch (error) {
      console.error('❌ Error adding sample data:', error);
    }
  };

  // Make addSampleData available globally for testing
  window.addSampleData = addSampleData;
  // Component loaded successfully (only log once)
  useEffect(() => {
  
  }, []);
  

  const [selectedGroup, setSelectedGroup] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const tab = params.get('tab');
      if (tab && GROUPS.some(g => g.key === tab)) return tab;
    } catch {}
    return GROUPS[0].key;
  });
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (params.get('tab') !== selectedGroup) {
      params.set('tab', selectedGroup);
      const base = window.location.hash.split('?')[0];
      const next = `${base}?${params.toString()}`;
      if (window.location.hash !== next) window.location.hash = next;
    }
  }, [selectedGroup]);

  // Respond to hash changes
  useEffect(() => {
    const handler = () => {
      try {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const tab = params.get('tab');
        if (tab && GROUPS.some(g => g.key === tab) && tab !== selectedGroup) {
          setSelectedGroup(tab);
        }
      } catch {}
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [selectedGroup]);
  const [filters, setFilters] = useState({ date: '', head: '', amount: '' });
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [amountFilter, setAmountFilter] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const exportRef = useRef();

  // Employees - Updated to be user-specific
  // Use employees from enhanced context instead of local state
  const employees = contextEmployees || [];
  const [employeeForm, setEmployeeForm] = useState({ 
    name: '', 
    personType: 'Employee',
    designation: '', 
    basicSalary: '', 
    bonus: '',
    contact: '', 
    aadhaar: '', 
    pan: '', 
    bankName: '',
    bankAccount: '', 
    ifsc: '', 
    address: '' 
  });
  const [employeeDocuments, setEmployeeDocuments] = useState({
    aadhaarCard: null,
    panCard: null,
    bankPassbook: null,
    photo: null,
    resume: null,
    otherDocuments: []
  });
  const [salaryForm, setSalaryForm] = useState({ employeeId: '', month: '', amount: '', remarks: '' });
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState({});
  const [editingEmployeeDocuments, setEditingEmployeeDocuments] = useState({
    aadhaarCard: null,
    panCard: null,
    bankPassbook: null,
    photo: null,
    resume: null,
    otherDocuments: []
  });

  // KYC validation states
  const [kycErrors, setKycErrors] = useState({
    contact: '',
    aadhaar: '',
    pan: '',
    bankName: '',
    bankAccount: '',
    ifsc: ''
  });
  const [showEditForm, setShowEditForm] = useState(false);

  // Expenses
  const [expenseForm, setExpenseForm] = useState({ 
    date: new Date().toISOString().split('T')[0], 
    head: '', 
    amount: '', 
    description: '', 
    receipt: null,
    paymentMode: 'Cash',
    constantEntry: '',
    personType: 'None', // 'None', 'Employee', 'Party'
    employeeId: '',
    partyId: '',
    personName: '',
    workOrderId: '',
    phaseId: '',
    costCategory: '',
  });
  const [expenses, setExpenses] = useState([]);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editingExpense, setEditingExpense] = useState({});
  
  // Parties (customers/vendors) for auto-fetching names
  const [parties, setParties] = useState([]);
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const [nameSearchText, setNameSearchText] = useState('');
  const nameDropdownRef = useRef(null);
  
  // Auto-suggest for irregular payments person names
  const [showIrregularNameDropdown, setShowIrregularNameDropdown] = useState(false);
  const irregularNameDropdownRef = useRef(null);
  
  // Filtered expenses based on search, date, and amount filters
  const tableFilteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const matchesSearch = !searchQuery ||
        expense.head?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        expense.constantEntry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        expense.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        expense.personName?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesDate = !dateFilter || expense.date === dateFilter;

      const matchesAmount = !amountFilter ||
        Math.abs(Number(expense.amount) - Number(amountFilter)) < 0.01;

      return matchesSearch && matchesDate && matchesAmount;
    });
  }, [expenses, searchQuery, dateFilter, amountFilter]);

  // Pagination & Sorting
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  // Table sorting and pagination for filtered expenses (default: newest date first)
  const { sortConfig, handleSort: tableHandleSort, getSortedData } = useTableSort([], {
    key: 'date',
    direction: 'desc',
  });
  const tableSortedExpenses = useMemo(
    () => getSortedData(tableFilteredExpenses),
    [tableFilteredExpenses, sortConfig.key, sortConfig.direction, getSortedData]
  );
  const tablePagination = useTablePagination(tableSortedExpenses, 10);



  // --- Salary Tab State ---
  const [fixedSalaryRows, setFixedSalaryRows] = useState([]);
  const [performanceSalaryRows, setPerformanceSalaryRows] = useState([]);
  const [salaryDeductions, setSalaryDeductions] = useState([]);
  const [salaryEmployeeId, setSalaryEmployeeId] = useState('');
  const [salaryDate, setSalaryDate] = useState('');
  const [salaryMonth, setSalaryMonth] = useState('');
  const [salarySearch, setSalarySearch] = useState('');
  const [salaryError, setSalaryError] = useState('');
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [salaryPaymentMode, setSalaryPaymentMode] = useState('Cash');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [selectedEmployeeIndex, setSelectedEmployeeIndex] = useState(-1);
  const [selectedEmployeeDisplay, setSelectedEmployeeDisplay] = useState('');
  const [editingSalaryPayment, setEditingSalaryPayment] = useState(null);
  const [showEditSalaryModal, setShowEditSalaryModal] = useState(false);
  const [isEditingSalary, setIsEditingSalary] = useState(false);
  
  // Salary adjustment from cash balance
  const [adjustSalaryFromBalance, setAdjustSalaryFromBalance] = useState(false);
  const [salaryBalanceSource, setSalaryBalanceSource] = useState('both'); // 'cash' | 'upi' | 'both'
  const [salaryBalanceAmount, setSalaryBalanceAmount] = useState(0);
  // Settle salary from a SPECIFIED other employee's wallet (based on their available balance)
  const [adjustSalaryFromOtherWallets, setAdjustSalaryFromOtherWallets] = useState(false);
  const [salarySourceEmployeeId, setSalarySourceEmployeeId] = useState(''); // specific employee whose wallet to use
  const [salarySourceEmployeeBalance, setSalarySourceEmployeeBalance] = useState(null); // { total, cash, upi }
  const [salaryOtherWalletsAmount, setSalaryOtherWalletsAmount] = useState(0);
  const [salaryBalanceSourceOther, setSalaryBalanceSourceOther] = useState('both'); // cash | upi | both for the source wallet
  
  // Aliases for new 4-column layout
  const deductionRows = salaryDeductions;
  const setDeductionRows = setSalaryDeductions;
  const selectedEmp = contextEmployees?.find(emp => emp.employeeId === salaryEmployeeId) || null;
  const setSelectedEmp = (emp) => {
    if (emp) {
      setSalaryEmployeeId(emp.employeeId);
    } else {
      setSalaryEmployeeId('');
    }
  };
  
  // Irregular payments (Labour/Advances)
  const [irregularPayments, setIrregularPayments] = useState([]);
  const [irregularForm, setIrregularForm] = useState({
    date: new Date().toISOString().split('T')[0],
    personType: 'Employee',
    employeeId: '',
    personName: '',
    paymentType: 'Advance',
    amount: '',
    remark: '',
    paymentMode: 'Cash',
    settlementRemark: '',
    workOrderId: '',
    phaseId: '',
    costCategory: '',
  });

  useEffect(() => {
    if (!ENABLE_WORK_ORDERS) return;
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const woId = params.get('workOrderId') || '';
      const phaseId = params.get('phaseId') || '';
      if (!woId) return;
      setExpenseForm((prev) => ({ ...prev, workOrderId: woId, phaseId }));
      setIrregularForm((prev) => ({ ...prev, workOrderId: woId, phaseId }));
    } catch (_) { /* ignore */ }
  }, []);

  // Labour/Advance – employee cash flow (record & settle from wallet)
  const [recordInEmployeeCashFlowLabour, setRecordInEmployeeCashFlowLabour] = useState(false);
  const [selectedEmployeeForCashFlowLabour, setSelectedEmployeeForCashFlowLabour] = useState('');
  const [markLabourSettledFromWallet, setMarkLabourSettledFromWallet] = useState(true); // default: mark as settled when recording from wallet
  const [labourSourceEmployeeBalance, setLabourSourceEmployeeBalance] = useState(null);

  // Settle old labour from wallet (back-date) modal
  const [settleLabourModalPayment, setSettleLabourModalPayment] = useState(null);
  const [settleLabourDate, setSettleLabourDate] = useState('');
  const [settleLabourEmployeeId, setSettleLabourEmployeeId] = useState('');
  const [settleLabourPaymentMode, setSettleLabourPaymentMode] = useState('cash'); // 'cash' | 'upi' - which wallet to deduct from
  const [settleLabourRemark, setSettleLabourRemark] = useState('');
  const [settleLabourEmployeeBalance, setSettleLabourEmployeeBalance] = useState(null);
  const [settleLabourSubmitting, setSettleLabourSubmitting] = useState(false);

  // Settle fixed/variable expense from employee wallet
  const [settleExpenseModal, setSettleExpenseModal] = useState(null);
  const [settleExpenseDate, setSettleExpenseDate] = useState('');
  const [settleExpenseEmployeeId, setSettleExpenseEmployeeId] = useState('');
  const [settleExpensePaymentMode, setSettleExpensePaymentMode] = useState('cash');
  const [settleExpenseRemark, setSettleExpenseRemark] = useState('');
  const [settleExpenseEmployeeBalance, setSettleExpenseEmployeeBalance] = useState(null);
  const [settleExpenseSubmitting, setSettleExpenseSubmitting] = useState(false);

  // Settle fixed/variable expense from wallet when creating a new entry
  const [recordSettleFromWalletExpense, setRecordSettleFromWalletExpense] = useState(false);
  const [selectedEmployeeForCashFlowExpense, setSelectedEmployeeForCashFlowExpense] = useState('');
  const [expenseSourceEmployeeBalance, setExpenseSourceEmployeeBalance] = useState(null);

  // Labour/Advances table: filter and sort (default = newest entry first)
  const [labourAdvanceFilter, setLabourAdvanceFilter] = useState({ search: '', personType: '', paymentType: '', settlementStatus: '' });
  const [labourAdvanceSort, setLabourAdvanceSort] = useState({ field: 'createdAt', order: 'desc' }); // newest entry first
  
  // Get unique person names from existing irregular payments for auto-suggest
  const uniquePersonNames = useMemo(() => {
    const namesMap = new Map(); // Use Map to store case-insensitive keys with original casing
    const seenLower = new Set(); // Track lowercase versions to avoid duplicates
    
    // Add names from irregular payments
    irregularPayments.forEach(payment => {
      if (payment.personName && payment.personName.trim()) {
        const trimmed = payment.personName.trim();
        const lower = trimmed.toLowerCase();
        if (!seenLower.has(lower)) {
          seenLower.add(lower);
          namesMap.set(lower, trimmed);
        }
      }
    });
    
    // Add employee names
    employees.forEach(emp => {
      if (emp.name && emp.name.trim()) {
        const trimmed = emp.name.trim();
        const lower = trimmed.toLowerCase();
        if (!seenLower.has(lower)) {
          seenLower.add(lower);
          namesMap.set(lower, trimmed);
        }
      }
    });
    
    // Add party names
    parties.forEach(party => {
      if (party.firmName && party.firmName.trim()) {
        const trimmed = party.firmName.trim();
        const lower = trimmed.toLowerCase();
        if (!seenLower.has(lower)) {
          seenLower.add(lower);
          namesMap.set(lower, trimmed);
        }
      }
      if (party.name && party.name.trim()) {
        const trimmed = party.name.trim();
        const lower = trimmed.toLowerCase();
        if (!seenLower.has(lower)) {
          seenLower.add(lower);
          namesMap.set(lower, trimmed);
        }
      }
    });
    
    // Return sorted array of unique names (preserving original casing)
    return Array.from(namesMap.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [irregularPayments, employees, parties]);
  
  // Filtered person names for irregular payments
  const filteredIrregularNames = useMemo(() => {
    if (!irregularForm.personName || irregularForm.personType === 'Employee') return [];
    const searchLower = irregularForm.personName.toLowerCase().trim();
    if (!searchLower) return [];
    
    // Filter and sort by relevance (exact matches first, then starts with, then contains)
    const matches = uniquePersonNames
      .filter(name => {
        const nameLower = name.toLowerCase();
        return nameLower.includes(searchLower);
      })
      .map(name => {
        const nameLower = name.toLowerCase();
        let priority = 3; // Default: contains
        if (nameLower === searchLower) {
          priority = 1; // Exact match
        } else if (nameLower.startsWith(searchLower)) {
          priority = 2; // Starts with
        }
        return { name, priority };
      })
      .sort((a, b) => {
        // Sort by priority first, then alphabetically
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        return a.name.localeCompare(b.name);
      })
      .map(item => item.name)
      .slice(0, 10);
    
    return matches;
  }, [irregularForm.personName, irregularForm.personType, uniquePersonNames]);

  // Labour/Advances: filtered and sorted rows (default newest entry first by date/createdAt)
  const labourAdvanceFilteredSortedRows = useMemo(() => {
    let list = [...irregularPayments];
    const { search, personType, paymentType, settlementStatus } = labourAdvanceFilter;
    const searchLower = (search || '').trim().toLowerCase();
    if (searchLower) {
      list = list.filter(item => {
        const personName = item.personType === 'Employee'
          ? (employees.find(e => e.employeeId === item.employeeId)?.name || item.employeeId || '')
          : (item.personName || '');
        return personName.toLowerCase().includes(searchLower);
      });
    }
    if (personType) list = list.filter(item => (item.personType || '') === personType);
    if (paymentType) list = list.filter(item => (item.paymentType || '') === paymentType);
    if (settlementStatus === 'settled') list = list.filter(item => !!item.settled);
    if (settlementStatus === 'unsettled') list = list.filter(item => !item.settled);
    const field = labourAdvanceSort.field;
    const order = labourAdvanceSort.order;
    const mult = order === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      let va; let vb;
      if (field === 'date') {
        va = a.date || '';
        vb = b.date || '';
        const cmp = (va > vb) ? 1 : (va < vb) ? -1 : 0;
        return mult * cmp;
      }
      if (field === 'createdAt') {
        va = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.date ? new Date(a.date).getTime() : 0);
        vb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.date ? new Date(b.date).getTime() : 0);
        return mult * (va - vb);
      }
      if (field === 'amount') {
        va = Number(a.amount) || 0;
        vb = Number(b.amount) || 0;
        return mult * (va - vb);
      }
      if (field === 'person') {
        const nameA = a.personType === 'Employee' ? (employees.find(e => e.employeeId === a.employeeId)?.name || a.employeeId || '') : (a.personName || '');
        const nameB = b.personType === 'Employee' ? (employees.find(e => e.employeeId === b.employeeId)?.name || b.employeeId || '') : (b.personName || '');
        return mult * (nameA.localeCompare(nameB, undefined, { sensitivity: 'base' }));
      }
      return 0;
    });
    return list;
  }, [irregularPayments, employees, labourAdvanceFilter, labourAdvanceSort]);

  // id -> 'Earning' | 'Deduction'
  const [appliedIrregularSelections, setAppliedIrregularSelections] = useState({});
  
  // Additional variables for 4-column layout
  const irregularPaymentsForEmployee = irregularPayments.filter(p => !p.applied && p.personType === 'Employee' && p.employeeId === salaryEmployeeId);
  
  // Calculate totals
  const totalEarnings = fixedSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0) + 
                       performanceSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0) +
                       irregularPaymentsForEmployee.reduce((sum, p) => sum + (appliedIrregularSelections[p.id] === 'Earning' ? (Number(p.amount) || 0) : 0), 0);
  
  const totalDeductions = salaryDeductions.reduce((sum, deduction) => sum + (Number(deduction.amount) || 0), 0) +
                         irregularPaymentsForEmployee.reduce((sum, p) => sum + (appliedIrregularSelections[p.id] === 'Deduction' ? (Number(p.amount) || 0) : 0), 0);
  
  const netSalary = totalEarnings - totalDeductions;
  
  // Irregular payment handlers
  const handleIrregularToggle = (idx, checked) => {
    const item = irregularPaymentsForEmployee[idx];
    if (checked) {
      setAppliedIrregularSelections(prev => ({ ...prev, [item.id]: 'Earning' }));
    } else {
      setAppliedIrregularSelections(prev => {
        const newSelections = { ...prev };
        delete newSelections[item.id];
        return newSelections;
      });
    }
  };
  
  const handleIrregularAppliedAsChange = (idx, value) => {
    const item = irregularPaymentsForEmployee[idx];
    setAppliedIrregularSelections(prev => ({ ...prev, [item.id]: value }));
  };

  const handleSalarySubmit = async (e) => {
    e.preventDefault();
    const err = validateSalaryForm({
      salaryEmployeeId,
      salaryDate,
      salaryMonth,
      performanceSalaryRows,
      salaryDeductions,
    });
    if (err) {
      setSalaryError(err);
      return;
    }
    setSalaryError('');
    const result = await submitSalaryPayment({
      db,
      companyId,
      appId,
      userId,
      auth,
      employees,
      contextEmployees,
      irregularPayments,
      salaryEmployeeId,
      salaryDate,
      salaryMonth,
      fixedSalaryRows,
      performanceSalaryRows,
      salaryDeductions,
      appliedIrregularSelections,
      salaryPaymentMode,
      adjustSalaryFromBalance,
      salaryBalanceAmount,
      salaryBalanceSource,
      adjustSalaryFromOtherWallets,
      salaryOtherWalletsAmount,
      salarySourceEmployeeId,
      salaryBalanceSourceOther,
      reloadSalaryPayments,
      success,
      warning,
    });
    if (!result.ok) {
      setSalaryError(result.error || 'Failed to save salary payment');
      return;
    }
    setFixedSalaryRows([]);
    setPerformanceSalaryRows([]);
    setSalaryDeductions([]);
    setSalaryEmployeeId('');
    setSelectedEmployeeDisplay('');
    setSalaryDate('');
    setSalaryMonth('');
    setSalaryPaymentMode('Cash');
    setAdjustSalaryFromBalance(false);
    setSalaryBalanceSource('both');
    setSalaryBalanceAmount(0);
    setAdjustSalaryFromOtherWallets(false);
    setSalarySourceEmployeeId('');
    setSalarySourceEmployeeBalance(null);
    setSalaryOtherWalletsAmount(0);
    setSalaryBalanceSourceOther('both');
    setAppliedIrregularSelections({});
  };

  const handleExportSalaryCSV = () => exportSalaryPaymentsCSV(salaryPayments);
  const handleExportSalaryExcel = () => exportSalaryPaymentsExcel(salaryPayments, currentCompany);
  const handleExportSalaryPDF = () => exportSalaryPaymentsPDF(salaryPayments, currentCompany, companyCurrency);

  const resetOtherWalletSettlement = () => {
    setSalaryOtherWalletsAmount(0);
    setSalarySourceEmployeeId('');
    setSalarySourceEmployeeBalance(null);
  };

  // Employee table sorting and pagination
  const [employeeFilters, setEmployeeFilters] = useState({ search: '', designation: '' });
  const [employeeSortKey, setEmployeeSortKey] = useState('name');
  const [employeeSortDir, setEmployeeSortDir] = useState('asc');
  const [employeeCurrentPage, setEmployeeCurrentPage] = useState(1);
  const [employeePageSize, setEmployeePageSize] = useState(10);
  // Manual refresh triggers for rare cases where realtime listener lags
  const reloadExpenses = async () => {
    try {
      if (!isAuthReady || !userId || !appId) return;
      const q = query(getCollectionForCompany(db, { companyId, appId, userId }, 'expenses'), where('group', '==', selectedGroup));
      const snap = await getDocs(q);
      const expenseData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExpenses(expenseData);
    } catch (e) {
      console.warn('reloadExpenses failed', e);
    }
  };
  const reloadSalaryPayments = async () => {
    try {
      if (!isAuthReady || !userId || !appId) return;
      const snap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'salaryPayments'));
      const payments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSalaryPayments(payments);
    } catch (e) {
      console.warn('reloadSalaryPayments failed', e);
    }
  };

  // --- Firestore listeners ---
  useEffect(() => {
    // Employees - Updated to be user-specific
    if (!isAuthReady || !userId || !appId) {
      console.log('Skipping employees listener - not ready:', { isAuthReady, userId, appId });
      return;
    }
    
    // Employees are now loaded from EnhancedCompanyContext
    // No need for separate listener here
  }, [isAuthReady, userId, appId]);

  useEffect(() => {
    // Expenses for selected group - Updated to be user-specific
    if (!isAuthReady || !userId || !appId) {
      console.log('Skipping expenses listener - not ready:', { isAuthReady, userId, appId });
      return;
    }
    
    const q = query(getCollectionForCompany(db, { companyId, appId, userId }, 'expenses'), where('group', '==', selectedGroup));
    const unsub = onSnapshot(q, snap => {
      const expenseData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExpenses(expenseData);
    }, (error) => {
      console.error('Error in expenses listener:', error);
    });
    return () => unsub();
  }, [selectedGroup, isAuthReady, userId, appId]);

  useEffect(() => {
    // Salary Payments - Updated to be user-specific
    if (!isAuthReady || !userId || !appId) {
      console.log('🔍 Salary payments listener skipped - not ready:', { isAuthReady, userId, appId, companyId });
      return;
    }
    
    console.log('🔍 Setting up salary payments listener for:', { companyId, appId, userId });
    const unsub = onSnapshot(getCollectionForCompany(db, { companyId, appId, userId }, 'salaryPayments'), snap => {
      const payments = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔍 Salary payments loaded:', payments.length, 'entries');
      setSalaryPayments(payments);
    }, (error) => {
      console.error('Error in salary payments listener:', error);
    });
    
    return () => unsub();
  }, [isAuthReady, userId, appId, companyId]);

  // Fetch source employee's wallet balance when user selects a source employee for salary settlement
  useEffect(() => {
    if (!adjustSalaryFromOtherWallets || !salarySourceEmployeeId || !companyId || !appId || !userId) {
      setSalarySourceEmployeeBalance(null);
      return;
    }
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const companyData = { companyId, appId, userId };
        const bal = await EmployeeCashManager.getEmployeeBalance(companyData, salarySourceEmployeeId);
        if (!cancelled) setSalarySourceEmployeeBalance(bal);
      } catch (e) {
        if (!cancelled) setSalarySourceEmployeeBalance(null);
        console.warn('Failed to fetch source employee balance', e);
      }
    };
    fetchBalance();
    return () => { cancelled = true; };
  }, [adjustSalaryFromOtherWallets, salarySourceEmployeeId, companyId, appId, userId]);

  // Fetch selected employee's wallet balance for Labour cash flow recording
  useEffect(() => {
    if (!recordInEmployeeCashFlowLabour || !selectedEmployeeForCashFlowLabour || !companyId || !appId || !userId) {
      setLabourSourceEmployeeBalance(null);
      return;
    }
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const companyData = { companyId, appId, userId };
        const bal = await EmployeeCashManager.getEmployeeBalance(companyData, selectedEmployeeForCashFlowLabour);
        if (!cancelled) setLabourSourceEmployeeBalance(bal);
      } catch (e) {
        if (!cancelled) setLabourSourceEmployeeBalance(null);
        console.warn('Failed to fetch labour cash flow employee balance', e);
      }
    };
    fetchBalance();
    return () => { cancelled = true; };
  }, [recordInEmployeeCashFlowLabour, selectedEmployeeForCashFlowLabour, companyId, appId, userId]);

  // Fetch balance for "settle old labour from wallet" modal
  useEffect(() => {
    if (!settleLabourModalPayment || !settleLabourEmployeeId || !companyId || !appId || !userId) {
      setSettleLabourEmployeeBalance(null);
      return;
    }
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const companyData = { companyId, appId, userId };
        const bal = await EmployeeCashManager.getEmployeeBalance(companyData, settleLabourEmployeeId);
        if (!cancelled) setSettleLabourEmployeeBalance(bal);
      } catch (e) {
        if (!cancelled) setSettleLabourEmployeeBalance(null);
      }
    };
    fetchBalance();
    return () => { cancelled = true; };
  }, [settleLabourModalPayment, settleLabourEmployeeId, companyId, appId, userId]);

  // Fetch balance for "settle expense from wallet" modal
  useEffect(() => {
    if (!settleExpenseModal || !settleExpenseEmployeeId || !companyId || !appId || !userId) {
      setSettleExpenseEmployeeBalance(null);
      return undefined;
    }
    let cancelled = false;
    const companyData = { companyId, appId, userId };
    (async () => {
      try {
        const bal = await EmployeeCashManager.getEmployeeBalance(companyData, settleExpenseEmployeeId);
        if (!cancelled) setSettleExpenseEmployeeBalance(bal);
      } catch {
        if (!cancelled) setSettleExpenseEmployeeBalance(null);
      }
    })();
    return () => { cancelled = true; };
  }, [settleExpenseModal, settleExpenseEmployeeId, companyId, appId, userId]);

  // Fetch wallet balance when settling a new expense from employee wallet
  useEffect(() => {
    if (!recordSettleFromWalletExpense || !selectedEmployeeForCashFlowExpense || !companyId || !appId || !userId) {
      setExpenseSourceEmployeeBalance(null);
      return undefined;
    }
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const companyData = { companyId, appId, userId };
        const bal = await EmployeeCashManager.getEmployeeBalance(
          companyData,
          selectedEmployeeForCashFlowExpense
        );
        if (!cancelled) setExpenseSourceEmployeeBalance(bal);
      } catch {
        if (!cancelled) setExpenseSourceEmployeeBalance(null);
      }
    };
    fetchBalance();
    return () => { cancelled = true; };
  }, [recordSettleFromWalletExpense, selectedEmployeeForCashFlowExpense, companyId, appId, userId]);

  useEffect(() => {
    // Irregular Payments - Labour/Advances listener
    if (!isAuthReady || !userId || !appId) {
      console.log('🔍 Irregular payments listener skipped - not ready:', { isAuthReady, userId, appId, companyId });
      return;
    }
    
    console.log('🔍 Setting up irregular payments listener for:', { companyId, appId, userId });
    const unsub = onSnapshot(getCollectionForCompany(db, { companyId, appId, userId }, 'irregularPayments'), snap => {
      const rows = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔍 Irregular payments loaded:', rows.length, 'entries');
      setIrregularPayments(rows);
    }, (error) => {
      console.error('Error in irregular payments listener:', error);
    });
    return () => unsub();
  }, [isAuthReady, userId, appId, companyId]);

  // Fetch parties for auto-fetching names in expenses
  useEffect(() => {
    if (!isAuthReady || !userId || !appId || !companyId) {
      console.log('🔍 Parties listener skipped - not ready:', { isAuthReady, userId, appId, companyId });
      return;
    }
    
    const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
    if (!partiesCollectionRef) return;
    
    console.log('🔍 Setting up parties listener for expenses');
    const unsub = onSnapshot(
      partiesCollectionRef,
      (snap) => {
        const allParties = [];
        snap.forEach((doc) => {
          allParties.push({ id: doc.id, ...doc.data() });
        });
        allParties.sort((a, b) => (a.firmName || '').localeCompare(b.firmName || ''));
        setParties(allParties);
        console.log('🔍 Parties loaded for expenses:', allParties.length, 'entries');
      },
      (error) => {
        console.error('Error in parties listener:', error);
      }
    );
    return () => unsub();
  }, [isAuthReady, userId, appId, companyId]);


  // --- Employee handlers ---
  const handleEmployeeFormChange = e => {
    const { name, value } = e.target;
    setEmployeeForm(f => ({ ...f, [name]: value }));
    
    // KYC validation
    if (name === 'contact' && value) {
      const validation = validateContact(value);
      setKycErrors(prev => ({ ...prev, contact: validation.isValid ? '' : validation.message }));
    } else if (name === 'aadhaar' && value) {
      const validation = validateAadhaar(value);
      setKycErrors(prev => ({ ...prev, aadhaar: validation.isValid ? '' : validation.message }));
    } else if (name === 'pan' && value) {
      const validation = validatePAN(value);
      setKycErrors(prev => ({ ...prev, pan: validation.isValid ? '' : validation.message }));
    } else if (name === 'bankAccount' && value) {
      const validation = validateBankAccount(value);
      setKycErrors(prev => ({ ...prev, bankAccount: validation.isValid ? '' : validation.message }));
    } else if (name === 'ifsc' && value) {
      const validation = validateIFSC(value);
      setKycErrors(prev => ({ ...prev, ifsc: validation.isValid ? '' : validation.message }));
    } else if (name === 'bankName' && value) {
      setKycErrors(prev => ({ ...prev, bankName: value.trim() ? '' : 'Bank name is required' }));
    }
    
  };

  const calculateTotalCTC = () =>
    calculateAnnualCTC(employeeForm.basicSalary, employeeForm.bonus);

  const calculateEditTotalCTC = () =>
    calculateAnnualCTC(editingEmployee.basicSalary, editingEmployee.bonus);

  // --- KYC Validation Functions ---
  const validateAadhaar = (aadhaar) => {
    // Remove spaces and dashes
    const cleanAadhaar = aadhaar.replace(/[\s-]/g, '');
    
    // Check if it's exactly 12 digits
    if (!/^\d{12}$/.test(cleanAadhaar)) {
      return { isValid: false, message: 'Aadhaar must be exactly 12 digits' };
    }
    
    // Check if it doesn't start with 0 or 1
    if (cleanAadhaar.startsWith('0') || cleanAadhaar.startsWith('1')) {
      return { isValid: false, message: 'Aadhaar cannot start with 0 or 1' };
    }
    
    // Verhoeff algorithm check (simplified)
    const digits = cleanAadhaar.split('').map(Number);
    const checkDigit = digits[digits.length - 1];
    const dataDigits = digits.slice(0, -1);
    
    // Simple validation: sum of first 11 digits should not be divisible by 10
    const sum = dataDigits.reduce((acc, digit) => acc + digit, 0);
    if (sum % 10 === 0) {
      return { isValid: false, message: 'Invalid Aadhaar number' };
    }
    
    return { isValid: true, message: 'Valid Aadhaar number' };
  };

  const validatePAN = (pan) => {
    // Remove spaces
    const cleanPAN = pan.replace(/\s/g, '').toUpperCase();
    
    // Check format: ABCDE1234F (5 letters + 4 digits + 1 letter)
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(cleanPAN)) {
      return { isValid: false, message: 'PAN must be in format: ABCDE1234F' };
    }
    
    // Check if it's not a fake PAN (common patterns)
    const fakePatterns = ['AAAAA0000A', 'BBBBB0000B', 'CCCCC0000C'];
    if (fakePatterns.includes(cleanPAN)) {
      return { isValid: false, message: 'Invalid PAN number' };
    }
    
    return { isValid: true, message: 'Valid PAN number' };
  };

  const validateIFSC = (ifsc) => {
    // Remove spaces
    const cleanIFSC = ifsc.replace(/\s/g, '').toUpperCase();
    
    // Check format: 4 letters + 7 alphanumeric
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(cleanIFSC)) {
      return { isValid: false, message: 'IFSC must be in format: ABCD0123456' };
    }
    
    return { isValid: true, message: 'Valid IFSC code' };
  };

  const validateBankAccount = (account) => {
    // Remove spaces and dashes
    const cleanAccount = account.replace(/[\s-]/g, '');
    
    // Check if it's 9-18 digits
    if (!/^\d{9,18}$/.test(cleanAccount)) {
      return { isValid: false, message: 'Bank account must be 9-18 digits' };
    }
    
    return { isValid: true, message: 'Valid bank account number' };
  };

  const validateContact = (contact) => {
    // Remove spaces, dashes, and plus
    const cleanContact = contact.replace(/[\s\-+]/g, '');
    
    // Check if it's 10 digits (Indian mobile)
    if (!/^\d{10}$/.test(cleanContact)) {
      return { isValid: false, message: 'Contact must be 10 digits' };
    }
    
    // Check if it starts with valid Indian mobile prefixes
    const validPrefixes = ['6', '7', '8', '9'];
    if (!validPrefixes.includes(cleanContact[0])) {
      return { isValid: false, message: 'Invalid mobile number prefix' };
    }
    
    return { isValid: true, message: 'Valid contact number' };
  };

  // --- Document upload handlers ---
  const handleDocumentUpload = (field, file) => {
    setEmployeeDocuments(prev => ({ ...prev, [field]: file }));
  };

  // --- Employee Selection Handlers (for Salary Tab) ---
  const handleEmployeeSelect = (employee) => {
    setSalaryEmployeeId(employee.value);
    setSelectedEmployeeDisplay(employee.label);
    setSalarySearch(''); // Clear search field
    setShowEmployeeDropdown(false);
    setSelectedEmployeeIndex(-1);
    
    // Find the selected employee and populate fixed salary rows
    const selectedEmp = employees.find(emp => emp.employeeId === employee.value);
    if (selectedEmp) {
      const monthlyRows = getMonthlySalaryRows(selectedEmp);
      setFixedSalaryRows(monthlyRows);
      // Don't automatically clear performance rows - let user manage them manually
    }
  };

  // Click outside handler for dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (showEmployeeDropdown && !event.target.closest('.employee-dropdown')) {
        setShowEmployeeDropdown(false);
        setSelectedEmployeeIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmployeeDropdown]);
  
  const handleDropdownToggle = () => {
    if (showEmployeeDropdown) {
      setShowEmployeeDropdown(false);
      setSelectedEmployeeIndex(-1);
    } else {
      setShowEmployeeDropdown(true);
      setSalarySearch(''); // Clear search when opening
    }
  };
  
  // Employee dropdown options (searchable) - computed at component level
  const validEmployees = employees.filter((emp) => emp.employeeId && isEmployeeActive(emp));
  const employeeOptions = validEmployees.map(emp => ({
    value: emp.employeeId,
    label: `${emp.name} (${emp.employeeId})${emp.designation ? ' - ' + emp.designation : ''}`,
    post: emp.designation || ''
  }));
  const filteredEmployeeOptions = salarySearch
    ? employeeOptions.filter(opt => opt.label.toLowerCase().includes(salarySearch.toLowerCase()))
    : employeeOptions;
  
  // Get selected employee for salary form (already defined above)

  const handleEmployeeKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
    e.preventDefault();
      setSelectedEmployeeIndex(prev => 
        prev < filteredEmployeeOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedEmployeeIndex(prev => 
        prev > 0 ? prev - 1 : filteredEmployeeOptions.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedEmployeeIndex >= 0 && filteredEmployeeOptions[selectedEmployeeIndex]) {
        handleEmployeeSelect(filteredEmployeeOptions[selectedEmployeeIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowEmployeeDropdown(false);
      setSelectedEmployeeIndex(-1);
    }
  };

  const handleMultipleDocuments = (files) => {
    setEmployeeDocuments(prev => ({ 
      ...prev, 
      otherDocuments: [...prev.otherDocuments, ...Array.from(files)]
    }));
  };

  const removeDocument = (field, index = null) => {
    if (index !== null) {
      setEmployeeDocuments(prev => ({
        ...prev,
        otherDocuments: prev.otherDocuments.filter((_, i) => i !== index)
      }));
    } else {
      setEmployeeDocuments(prev => ({ ...prev, [field]: null }));
    }
  };

  // --- Employee Add Handler (MOVED TO EMPLOYEE MANAGEMENT PAGE) ---
  const handleAddEmployee = async e => {
    e.preventDefault();
    console.log('Employee management moved to Employee Management page');
    info('Employee management has been moved to the Employee Management page. Please use that page to add employees.', { title: 'Employee Management' });
    return;
    
    /* This entire function is no longer used - employees managed from Employee Management page
    const newId = generateEmployeeId(employees);
    const basic = Number(employeeForm.basicSalary || 0);
    
    // Calculate HRA based on type
    let hra = 0;
    if (employeeForm.hraType === 'percentage') {
      const hraPercentage = Number(employeeForm.hra) || 0;
      hra = basic * (hraPercentage / 100);
    } else {
      hra = Number(employeeForm.hra) || 0;
    }
    
    // Calculate Conveyance based on type
    let conveyance = 0;
    if (employeeForm.conveyanceAllowanceType === 'percentage') {
      const conveyancePercentage = Number(employeeForm.conveyanceAllowance) || 0;
      conveyance = basic * (conveyancePercentage / 100);
    } else {
      conveyance = Number(employeeForm.conveyanceAllowance) || 0;
    }
    
    // Calculate Employer PF based on type
    let pf = 0;
    if (employeeForm.employerPFType === 'percentage') {
      const pfPercentage = Number(employeeForm.employerPF) || 0;
      pf = basic * (pfPercentage / 100);
    } else {
      pf = Number(employeeForm.employerPF) || 0;
    }
    
    // Calculate Gratuity based on type
    let gratuity = 0;
    if (employeeForm.gratuityType === 'percentage') {
      const gratuityPercentage = Number(employeeForm.gratuity) || 0;
      gratuity = basic * (gratuityPercentage / 100);
    } else {
      gratuity = Number(employeeForm.gratuity) || 0;
    }
    
    // Calculate Special Allowance based on type
    let special = 0;
    if (employeeForm.specialAllowanceType === 'percentage') {
      const specialPercentage = Number(employeeForm.specialAllowance) || 0;
      special = basic * (specialPercentage / 100);
    } else {
      special = Number(employeeForm.specialAllowance) || 0;
    }
    
    const totalSalary = basic + hra + conveyance + pf + gratuity + special;
    
    // Upload documents to Firebase Storage
    const documentUrls = {};
    
    try {
      // Upload individual documents
      const uploadPromises = [];
      
      if (employeeDocuments.aadhaarCard) {
        const aadhaarRef = ref(storage, `employees/${newId}/aadhaar_${Date.now()}.pdf`);
        uploadPromises.push(uploadBytes(aadhaarRef, employeeDocuments.aadhaarCard).then(() => getDownloadURL(aadhaarRef)));
      }
      
      if (employeeDocuments.panCard) {
        const panRef = ref(storage, `employees/${newId}/pan_${Date.now()}.pdf`);
        uploadPromises.push(uploadBytes(panRef, employeeDocuments.panCard).then(() => getDownloadURL(panRef)));
      }
      
      if (employeeDocuments.bankPassbook) {
        const bankRef = ref(storage, `employees/${newId}/bank_${Date.now()}.pdf`);
        uploadPromises.push(uploadBytes(bankRef, employeeDocuments.bankPassbook).then(() => getDownloadURL(bankRef)));
      }
      
      if (employeeDocuments.photo) {
        const photoRef = ref(storage, `employees/${newId}/photo_${Date.now()}.jpg`);
        uploadPromises.push(uploadBytes(photoRef, employeeDocuments.photo).then(() => getDownloadURL(photoRef)));
      }
      
      if (employeeDocuments.resume) {
        const resumeRef = ref(storage, `employees/${newId}/resume_${Date.now()}.pdf`);
        uploadPromises.push(uploadBytes(resumeRef, employeeDocuments.resume).then(() => getDownloadURL(resumeRef)));
      }
      
      // Upload multiple other documents
      employeeDocuments.otherDocuments.forEach((doc, index) => {
        const otherRef = ref(storage, `employees/${newId}/other_${index}_${Date.now()}.pdf`);
        uploadPromises.push(uploadBytes(otherRef, doc).then(() => getDownloadURL(otherRef)));
      });
      
      const urls = await Promise.all(uploadPromises);
      
      // Map URLs to document fields
      let urlIndex = 0;
      if (employeeDocuments.aadhaarCard) documentUrls.aadhaarUrl = urls[urlIndex++];
      if (employeeDocuments.panCard) documentUrls.panUrl = urls[urlIndex++];
      if (employeeDocuments.bankPassbook) documentUrls.bankUrl = urls[urlIndex++];
      if (employeeDocuments.photo) documentUrls.photoUrl = urls[urlIndex++];
      if (employeeDocuments.resume) documentUrls.resumeUrl = urls[urlIndex++];
      documentUrls.otherUrls = urls.slice(urlIndex);
      
    } catch (error) {
      console.error('Error uploading documents:', error);
      error('Error uploading documents. Please try again.', { title: 'Upload Failed' });
      return;
    }
    
    // Save employee data with document URLs and calculated amounts
    const totalCTC = calculateTotalCTC();
    await addDoc(getCollectionForCompany(db, { companyId, appId, userId }, 'employees'), { 
      ...employeeForm, 
      salary: totalCTC,
      employeeId: newId, 
      documents: documentUrls,
      createdAt: serverTimestamp(),
      // Save calculated actual amounts for salary page usage
      basicSalaryAmount: basic,
      hraAmount: hra,
      conveyanceAmount: conveyance,
      employerPFAmount: pf,
      gratuityAmount: gratuity,
      specialAllowanceAmount: special,
      totalCTC: totalCTC
    });
    
    // Show success notification
    success(
      `Employee ${employeeForm.name} added successfully!`,
      {
        title: 'Employee Added',
        details: `Employee ID: ${newId} • ${employeeForm.designation || 'No designation'}`,
        duration: 6000
      }
    );
    
            // Reset form
        setEmployeeForm({
          name: '',
          personType: 'Employee',
          designation: '',
          basicSalary: '',
          hra: '',
          hraType: 'amount',
          conveyanceAllowance: '',
          conveyanceAllowanceType: 'amount',
          employerPF: '',
          employerPFType: 'amount',
          gratuity: '',
          gratuityType: 'amount',
          specialAllowance: '',
          specialAllowanceType: 'amount',
          contact: '',
          aadhaar: '',
          pan: '',
          bankName: '',
          bankAccount: '',
          ifsc: '',
          address: ''
        });
    setEmployeeDocuments({
      aadhaarCard: null,
      panCard: null,
      bankPassbook: null,
      photo: null,
      resume: null,
      otherDocuments: []
    });
    */ // End of commented out employee add function
  };
  
  // --- Fix existing employees without employeeId ---
  const fixExistingEmployees = async () => {
    console.log('Total employees:', employees.length);
    console.log('All employees data:', JSON.stringify(employees, null, 2));
    
    const employeesWithoutId = employees.filter(emp => !emp.employeeId);
    console.log('Employees without ID:', employeesWithoutId);
    
    // Employee ID assignment moved to Employee Management page
    console.log('Employee ID assignment handled by Employee Management system');
    // for (const emp of employeesWithoutId) {
    //   const newId = generateEmployeeId(employees);
    //   await updateDoc(doc(getCollectionForCompany(db, { companyId, appId, userId }, 'employees'), emp.id), { employeeId: newId });
    //   console.log(`Fixed employee ${emp.name} with ID: ${newId}`);
    // }
  };
  
  // --- Employee table sorting and pagination logic ---
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const matchesSearch = !employeeFilters.search || 
        emp.name?.toLowerCase().includes(employeeFilters.search.toLowerCase()) ||
        emp.employeeId?.toLowerCase().includes(employeeFilters.search.toLowerCase());
      const matchesDesignation = !employeeFilters.designation || emp.designation === employeeFilters.designation;
      return matchesSearch && matchesDesignation;
    });
  }, [employees, employeeFilters]);

  const sortedEmployees = useMemo(() => {
    return [...filteredEmployees].sort((a, b) => {
      let aVal = a[employeeSortKey] || '';
      let bVal = b[employeeSortKey] || '';
      
      if (employeeSortKey === 'salary') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (aVal < bVal) return employeeSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return employeeSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredEmployees, employeeSortKey, employeeSortDir]);

  const employeeTotalPages = Math.ceil(sortedEmployees.length / employeePageSize);
  const paginatedEmployees = useMemo(() => {
    const start = (employeeCurrentPage - 1) * employeePageSize;
    return sortedEmployees.slice(start, start + employeePageSize);
  }, [sortedEmployees, employeeCurrentPage, employeePageSize]);

  const handleEmployeeSort = (key) => {
    if (employeeSortKey === key) {
      setEmployeeSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setEmployeeSortKey(key);
      setEmployeeSortDir('asc');
    }
    setEmployeeCurrentPage(1);
  };

  const renderSortIcon = (key) => {
    if (employeeSortKey !== key) return '⇅';
    return employeeSortDir === 'asc' ? '↑' : '↓';
  };

  // Reset pagination when filters change
  useEffect(() => {
    setEmployeeCurrentPage(1);
  }, [employeeFilters, employeePageSize]);

  // --- Employee action handlers ---
  const handleViewEmployee = (emp) => {
    const documents = emp.documents || {};
    const documentLinks = [];
    
    if (documents.aadhaarUrl) documentLinks.push(`Aadhaar: ${documents.aadhaarUrl}`);
    if (documents.panUrl) documentLinks.push(`PAN: ${documents.panUrl}`);
    if (documents.bankUrl) documentLinks.push(`Bank Passbook: ${documents.bankUrl}`);
    if (documents.photoUrl) documentLinks.push(`Photo: ${documents.photoUrl}`);
    if (documents.resumeUrl) documentLinks.push(`Resume: ${documents.resumeUrl}`);
    if (documents.otherUrls) {
      documents.otherUrls.forEach((url, index) => {
        documentLinks.push(`Other Doc ${index + 1}: ${url}`);
      });
    }
    
    const documentText = documentLinks.length > 0 ? '\n\nDocuments:\n' + documentLinks.join('\n') : '\n\nNo documents uploaded';
    
    info(`Employee Details:\n\nID: ${emp.employeeId}\nName: ${emp.name}\nPost: ${emp.designation}\nBasic (annual): ${formatCurrency(getAnnualBasic(emp), companyCurrency)}\nBonus (annual): ${formatCurrency(getAnnualBonus(emp), companyCurrency)}\nTotal CTC: ${formatCurrency(getAnnualCTC(emp), companyCurrency)}\nContact: ${emp.contact}\nAadhaar: ${emp.aadhaar}\nPAN: ${emp.pan}\nBank: ${emp.bankAccount}\nIFSC: ${emp.ifsc}\nAddress: ${emp.address}${documentText}`, { title: 'Employee Details', duration: 15000 });
  };
  
  const handleExportEmployee = (emp) => {
    const basicSalary = getAnnualBasic(emp);
    const bonus = getAnnualBonus(emp);
    const total = getAnnualCTC(emp);
    const monthlySalary = Math.round(total / 12);
    const csvContent = [
      ['Employee ID', 'Name', 'Post', 'Basic Salary (Annual)', 'Bonus (Annual)', 'Total CTC', 'Monthly Salary'],
      [emp.employeeId, emp.name, emp.designation, basicSalary, bonus, total, monthlySalary]
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee_${emp.employeeId}_salary_breakdown.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const handleDeleteEmployee = async id => {
    const emp = employees.find(e => e.id === id);
    if (emp && window.confirm(`Are you sure you want to delete employee ${emp.name}? This action cannot be undone.`)) {
      try {
        const result = await deleteEmployee(id);
        if (!result?.success) {
          throw new Error(result?.error || 'Failed to delete employee');
        }
        success(
          'Employee deleted successfully!',
          {
            title: 'Employee Deleted',
            details: `${emp.name} • ${emp.employeeId || 'No ID'}`,
            duration: 6000
          }
        );
      } catch (error) {
        console.error('Error deleting employee:', error);
        error('Error deleting employee. Please try again.', { title: 'Delete Failed', duration: 7000 });
      }
    }
  };


  const handleEditEmployee = emp => {
    setEditingEmployeeId(emp.id);
    const bonusVal =
      emp.bonusAmount != null && emp.bonusAmount !== ''
        ? String(emp.bonusAmount)
        : (emp.bonus != null && emp.bonus !== '' ? String(emp.bonus) : '');
    const basicVal =
      emp.basicSalary != null && emp.basicSalary !== ''
        ? String(emp.basicSalary)
        : (emp.basicSalaryAmount != null ? String(emp.basicSalaryAmount) : '');
    setEditingEmployee({ 
      ...emp,
      basicSalary: basicVal,
      bonus: bonusVal,
      bankName: emp.bankName || ''
    });
    setShowEditForm(true);
  };
  const handleEditingEmployeeChange = e => {
    const { name, value } = e.target;
    setEditingEmployee(f => ({ ...f, [name]: value }));
    
    // KYC validation for edit form
    if (name === 'contact' && value) {
      const validation = validateContact(value);
      setKycErrors(prev => ({ ...prev, contact: validation.isValid ? '' : validation.message }));
    } else if (name === 'aadhaar' && value) {
      const validation = validateAadhaar(value);
      setKycErrors(prev => ({ ...prev, aadhaar: validation.isValid ? '' : validation.message }));
    } else if (name === 'pan' && value) {
      const validation = validatePAN(value);
      setKycErrors(prev => ({ ...prev, pan: validation.isValid ? '' : validation.message }));
    } else if (name === 'bankAccount' && value) {
      const validation = validateBankAccount(value);
      setKycErrors(prev => ({ ...prev, bankAccount: validation.isValid ? '' : validation.message }));
    } else if (name === 'ifsc' && value) {
      const validation = validateIFSC(value);
      setKycErrors(prev => ({ ...prev, ifsc: validation.isValid ? '' : validation.message }));
    } else if (name === 'bankName' && value) {
      setKycErrors(prev => ({ ...prev, bankName: value.trim() ? '' : 'Bank name is required' }));
    }
  };
  const handleSaveEmployee = async id => {
    const salaryPayload = buildSalaryPayload(editingEmployee.basicSalary, editingEmployee.bonus);
    const totalSalary = salaryPayload.ctc;
    
    // Upload new documents if any
    const documentUrls = { ...editingEmployee.documents };
    
    try {
      const uploadPromises = [];
      
      if (editingEmployeeDocuments.aadhaarCard) {
        const aadhaarRef = ref(storage, `employees/${editingEmployee.employeeId}/aadhaar_${Date.now()}.pdf`);
        const url = await uploadBytes(aadhaarRef, editingEmployeeDocuments.aadhaarCard).then(() => getDownloadURL(aadhaarRef));
        documentUrls.aadhaarUrl = url;
      }
      
      if (editingEmployeeDocuments.panCard) {
        const panRef = ref(storage, `employees/${editingEmployee.employeeId}/pan_${Date.now()}.pdf`);
        const url = await uploadBytes(panRef, editingEmployeeDocuments.panCard).then(() => getDownloadURL(panRef));
        documentUrls.panUrl = url;
      }
      
      if (editingEmployeeDocuments.bankPassbook) {
        const bankRef = ref(storage, `employees/${editingEmployee.employeeId}/bank_${Date.now()}.pdf`);
        const url = await uploadBytes(bankRef, editingEmployeeDocuments.bankPassbook).then(() => getDownloadURL(bankRef));
        documentUrls.bankUrl = url;
      }
      
      if (editingEmployeeDocuments.photo) {
        const photoRef = ref(storage, `employees/${editingEmployee.employeeId}/photo_${Date.now()}.jpg`);
        const url = await uploadBytes(photoRef, editingEmployeeDocuments.photo).then(() => getDownloadURL(photoRef));
        documentUrls.photoUrl = url;
      }
      
      if (editingEmployeeDocuments.resume) {
        const resumeRef = ref(storage, `employees/${editingEmployee.employeeId}/resume_${Date.now()}.pdf`);
        const url = await uploadBytes(resumeRef, editingEmployeeDocuments.resume).then(() => getDownloadURL(resumeRef));
        documentUrls.resumeUrl = url;
      }
      
      // Upload multiple other documents
      for (let i = 0; i < editingEmployeeDocuments.otherDocuments.length; i++) {
        const doc = editingEmployeeDocuments.otherDocuments[i];
        const otherRef = ref(storage, `employees/${editingEmployee.employeeId}/other_${i}_${Date.now()}.pdf`);
        const url = await uploadBytes(otherRef, doc).then(() => getDownloadURL(otherRef));
        if (!documentUrls.otherUrls) documentUrls.otherUrls = [];
        documentUrls.otherUrls.push(url);
      }
      
    } catch (error) {
      console.error('Error uploading documents:', error);
      error('Error uploading documents. Please try again.', { title: 'Upload Failed' });
      return;
    }
    
    // Filter out undefined values to prevent Firebase errors
    // Filter out undefined values to prevent Firebase errors
    const updateData = {
      name: editingEmployee.name,
      personType: editingEmployee.personType || 'Employee',
      designation: editingEmployee.designation,
      basicSalary: editingEmployee.basicSalary,
      bonus: editingEmployee.bonus || '',
      salary: totalSalary,
      contact: editingEmployee.contact,
      aadhaar: editingEmployee.aadhaar,
      pan: editingEmployee.pan,
      bankName: editingEmployee.bankName,
      bankAccount: editingEmployee.bankAccount,
      ifsc: editingEmployee.ifsc,
      address: editingEmployee.address,
      documents: documentUrls,
      ...salaryPayload,
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

          await updateDoc(doc(getCollectionForCompany(db, { companyId, appId, userId }, 'employees'), id), updateData);
    
    // Get employee details for notification
    const updatedEmployee = employees.find(e => e.id === id);
    const employeeName = updatedEmployee?.name || editingEmployee?.name || 'Employee';
    const employeeId = updatedEmployee?.employeeId || editingEmployee?.employeeId || '';
    
    // Show success notification
    success(
      `Employee ${employeeName} updated successfully!`,
      {
        title: 'Employee Updated',
        details: employeeId ? `Employee ID: ${employeeId} • ${updatedEmployee?.designation || 'No designation'}` : 'Employee details updated',
        duration: 6000
      }
    );
    
    setEditingEmployeeId(null);
    setEditingEmployee({});
    setEditingEmployeeDocuments({
      aadhaarCard: null,
      panCard: null,
      bankPassbook: null,
      photo: null,
      resume: null,
      otherDocuments: []
    });
    setShowEditForm(false);
    success('Employee updated successfully!', { title: 'Success' });
  };
  const handleCancelEditEmployee = () => {
    setEditingEmployeeId(null);
    setEditingEmployee({});
    setEditingEmployeeDocuments({
      aadhaarCard: null,
      panCard: null,
      bankPassbook: null,
      photo: null,
      resume: null,
      otherDocuments: []
    });
    setShowEditForm(false);
  };

  // --- Salary payment handlers ---
  const handleSalaryFormChange = e => {
    const { name, value } = e.target;
    setSalaryForm(f => ({ ...f, [name]: value }));
  };
  const handleAddSalaryPayment = async e => {
    e.preventDefault();
    if (!salaryForm.employeeId || !salaryForm.month || !salaryForm.amount) return;
    const emp = employees.find(e => e.id === salaryForm.employeeId);
    await addDoc(getCollectionForCompany(db, { companyId, appId, userId }, 'expenses'), {
      group: 'salaries',
      employeeId: salaryForm.employeeId,
      employeeName: emp?.name || '',
      month: salaryForm.month,
      amount: Number(salaryForm.amount),
      remarks: salaryForm.remarks,
      date: new Date().toISOString().slice(0, 10),
      createdAt: serverTimestamp(),
    });
    setSalaryForm({ employeeId: '', month: '', amount: '', remarks: '' });
  };

  // --- Expense form handlers (fixed/variable) ---
  const handleExpenseFormChange = e => {
    const { name, value, files } = e.target;
    if (name === 'head') {
      // Reset constant entry when category changes
      setExpenseForm(f => ({ ...f, [name]: value, constantEntry: '' }));
    } else if (name === 'personType') {
      // Reset name fields when person type changes
      setExpenseForm(f => ({ 
        ...f, 
        [name]: value, 
        employeeId: '', 
        partyId: '', 
        personName: '' 
      }));
      setNameSearchText('');
    } else if (name === 'employeeId') {
      const selectedEmp = employees.find(e => e.employeeId === value);
      setExpenseForm(f => ({ 
        ...f, 
        employeeId: value,
        personName: selectedEmp?.name || ''
      }));
      setNameSearchText(selectedEmp ? `${selectedEmp.name} (${selectedEmp.employeeId})` : '');
      setShowNameDropdown(false);
    } else if (name === 'partyId') {
      const selectedParty = parties.find(p => p.id === value);
      setExpenseForm(f => ({ 
        ...f, 
        partyId: value,
        personName: selectedParty?.firmName || selectedParty?.name || ''
      }));
      setNameSearchText(selectedParty ? (selectedParty.firmName || selectedParty.name) : '');
      setShowNameDropdown(false);
    } else if (name === 'nameSearchText') {
      setNameSearchText(value);
      setShowNameDropdown(true);
    } else if (name === 'receipt') {
      if (e.target.clearReceipt) {
        setExpenseForm((f) => ({ ...f, receipt: null }));
      } else {
        setExpenseForm((f) => ({ ...f, receipt: files?.length ? files[0] : null }));
      }
    } else {
      setExpenseForm(f => ({ ...f, [name]: files ? files[0] : value }));
    }
  };
  
  // Handle name selection from dropdown
  const handleNameSelect = (type, id, name) => {
    if (type === 'employee') {
      setExpenseForm(f => ({ 
        ...f, 
        personType: 'Employee',
        employeeId: id,
        partyId: '',
        personName: name
      }));
      const emp = employees.find(e => e.employeeId === id);
      setNameSearchText(emp ? `${emp.name} (${emp.employeeId})` : name);
    } else if (type === 'party') {
      setExpenseForm(f => ({ 
        ...f, 
        personType: 'Party',
        partyId: id,
        employeeId: '',
        personName: name
      }));
      setNameSearchText(name);
    }
    setShowNameDropdown(false);
  };
  
  // Filtered names for dropdown
  const filteredNames = useMemo(() => {
    if (!nameSearchText) return [];
    const searchLower = nameSearchText.toLowerCase();
    const results = [];
    
    // Add employees
    employees
      .filter(emp => emp.name && emp.employeeId)
      .filter(emp => 
        emp.name.toLowerCase().includes(searchLower) || 
        emp.employeeId.toLowerCase().includes(searchLower)
      )
      .forEach(emp => {
        results.push({
          type: 'employee',
          id: emp.employeeId,
          name: emp.name,
          display: `${emp.name} (${emp.employeeId})${emp.designation ? ' - ' + emp.designation : ''}`
        });
      });
    
    // Add parties
    parties
      .filter(party => party.firmName || party.name)
      .filter(party => 
        (party.firmName || '').toLowerCase().includes(searchLower) ||
        (party.name || '').toLowerCase().includes(searchLower)
      )
      .forEach(party => {
        results.push({
          type: 'party',
          id: party.id,
          name: party.firmName || party.name,
          display: party.firmName || party.name
        });
      });
    
    return results.slice(0, 10); // Limit to 10 results
  }, [nameSearchText, employees, parties]);
  
  // Close name dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNameDropdown && nameDropdownRef.current && !nameDropdownRef.current.contains(event.target)) {
        setShowNameDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNameDropdown]);
  const handleAddExpense = async e => {
    e.preventDefault();
    if (!expenseForm.date || !expenseForm.head || !expenseForm.amount) return;

    const companyData = { companyId, appId, userId };
    const amountNum = Number(expenseForm.amount);
    const categoryLabel = selectedGroup === 'variable' ? 'Variable Expense' : 'Fixed Expense';
    const entryLabel = [expenseForm.head, expenseForm.constantEntry].filter(Boolean).join(' - ');
    const personLabel = expenseForm.personName || '—';

    if (recordSettleFromWalletExpense) {
      if (!selectedEmployeeForCashFlowExpense) {
        warning('Select an employee wallet to settle this expense.');
        return;
      }
      const selectedWalletEmployee = employees?.find((emp) => emp.id === selectedEmployeeForCashFlowExpense);
      if (!selectedWalletEmployee || !isEmployeeActive(selectedWalletEmployee)) {
        warning('Selected employee is inactive. Choose an active employee wallet.', {
          title: 'Inactive Employee',
          duration: 5000,
        });
        return;
      }
      const paymentModeRaw = (expenseForm.paymentMode || 'Cash').toLowerCase();
      const paymentMode = paymentModeRaw === 'upi' ? 'upi' : 'cash';
      const balance = expenseSourceEmployeeBalance || { cash: 0, upi: 0, total: 0 };
      const available =
        paymentMode === 'cash' ? balance.cash : paymentMode === 'upi' ? balance.upi : balance.total;
      if (available < amountNum) {
        warning(`Insufficient wallet balance. Available: ₹${(available || 0).toLocaleString('en-IN')}`, {
          title: 'Employee wallet',
          duration: 5000,
        });
        return;
      }
    }

    try {
      let receiptData = null;
      if (expenseForm.receipt) {
        const file = expenseForm.receipt;
        const storageRef = ref(storage, `receipts/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const receiptUrl = await getDownloadURL(storageRef);
        receiptData = {
          url: receiptUrl,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
        };
      }

      const expenseData = {
        group: selectedGroup,
        date: expenseForm.date,
        head: expenseForm.head,
        amount: amountNum,
        description: expenseForm.description,
        paymentMode: expenseForm.paymentMode,
        constantEntry: expenseForm.constantEntry,
        receiptData,
        settled: false,
        createdAt: serverTimestamp(),
      };

      if (expenseForm.personType === 'Employee' && expenseForm.employeeId) {
        expenseData.personType = 'Employee';
        expenseData.employeeId = expenseForm.employeeId;
        expenseData.personName = expenseForm.personName;
      } else if (expenseForm.personType === 'Party' && expenseForm.partyId) {
        expenseData.personType = 'Party';
        expenseData.partyId = expenseForm.partyId;
        expenseData.personName = expenseForm.personName;
      }
      appendWorkOrderFields(expenseData, expenseForm);

      const expenseRef = await addDoc(getCollectionForCompany(db, companyData, 'expenses'), expenseData);
      const expenseId = expenseRef.id;
      await syncLedgerFromSource(db, companyCtx, {
        sourceType: LEDGER_SOURCE_TYPES.expense,
        sourceCollection: 'expenses',
        sourceId: expenseId,
        doc: { ...expenseData, id: expenseId },
      });

      let settledViaWallet = false;
      if (recordSettleFromWalletExpense && selectedEmployeeForCashFlowExpense) {
        const selectedEmployee = employees?.find((emp) => emp.id === selectedEmployeeForCashFlowExpense);
        const paymentModeRaw = (expenseForm.paymentMode || 'Cash').toLowerCase();
        const paymentMode = paymentModeRaw === 'upi' ? 'upi' : 'cash';
        const transactionData = {
          employeeId: selectedEmployeeForCashFlowExpense,
          employeeName: selectedEmployee?.name || '',
          transactionType: 'outflow',
          amount: amountNum,
          paymentMode,
          category: categoryLabel,
          description: `Settle: ${entryLabel || categoryLabel} - ${personLabel}`,
          linkedPaymentId: expenseId,
          linkedPaymentType: 'expense',
          date: expenseForm.date || new Date().toISOString().split('T')[0],
        };
        const cashResult = await EmployeeCashManager.createCashTransaction(companyData, transactionData);
        if (!cashResult.success) {
          warning(`Expense saved as unsettled but wallet deduction failed: ${cashResult.error}`, {
            title: 'Cash flow',
            duration: 6000,
          });
        } else if (selectedEmployee) {
          await updateDoc(doc(getCollectionForCompany(db, companyData, 'expenses'), expenseId), {
            settled: true,
            appliedInMonth: (expenseForm.date || '').slice(0, 7),
            settlementRemark: 'Settled via employee wallet on entry',
            settledAt: serverTimestamp(),
            settledFromOtherWallet: {
              amount: amountNum,
              sourceEmployeeId: selectedEmployeeForCashFlowExpense,
              sourceEmployeeName: selectedEmployee.name || '',
            },
          });
          settledViaWallet = true;
        }
      }

      const details = [
        `${expenseData.date} • ${expenseData.head} • ${expenseData.paymentMode || 'Cash'}`,
        settledViaWallet ? 'Settled from employee wallet' : 'Saved as unsettled',
      ].join(' • ');

      success(`Expense of ${formatCurrency(amountNum, companyCurrency)} added successfully!`, {
        title: 'Expense Added',
        details,
        duration: 6000,
      });

      await reloadExpenses();
      setExpenseForm({
        date: new Date().toISOString().split('T')[0],
        head: '',
        amount: '',
        description: '',
        receipt: null,
        paymentMode: 'Cash',
        constantEntry: '',
        personType: 'None',
        employeeId: '',
        partyId: '',
        personName: '',
        workOrderId: '',
        phaseId: '',
        costCategory: '',
      });
      setNameSearchText('');
      setRecordSettleFromWalletExpense(false);
      setSelectedEmployeeForCashFlowExpense('');
      setExpenseSourceEmployeeBalance(null);
    } catch (err) {
      console.error('Failed to add expense', err);
      error('Failed to save expense. Please try again.');
    }
  };
  const handleEditExpense = exp => {
    setEditingExpenseId(exp.id);
    setEditingExpense({ ...exp });
    // Also update the expense form for the table edit functionality
    const personType = exp.personType || (exp.employeeId ? 'Employee' : exp.partyId ? 'Party' : 'None');
    let nameDisplay = '';
    if (personType === 'Employee' && exp.employeeId) {
      const emp = employees.find(e => e.employeeId === exp.employeeId);
      nameDisplay = emp ? `${emp.name} (${emp.employeeId})` : (exp.personName || '');
    } else if (personType === 'Party' && exp.partyId) {
      const party = parties.find(p => p.id === exp.partyId);
      nameDisplay = party ? (party.firmName || party.name) : (exp.personName || '');
    }
    
    setExpenseForm({
      date: exp.date || '',
      head: exp.head || '',
      amount: exp.amount || '',
      description: exp.description || '',
      receipt: exp.receipt || null,
      paymentMode: exp.paymentMode || 'Cash',
      constantEntry: exp.constantEntry || '',
      personType: personType,
      employeeId: exp.employeeId || '',
      partyId: exp.partyId || '',
      personName: exp.personName || ''
    });
    setNameSearchText(nameDisplay);
  };
  const handleEditingExpenseChange = e => {
    const { name, value, files } = e.target;
    if (name === 'head') {
      // Reset constant entry when category changes
      setEditingExpense(f => ({ ...f, [name]: value, constantEntry: '' }));
      setExpenseForm(f => ({ ...f, [name]: value, constantEntry: '' }));
    } else if (name === 'personType') {
      // Reset name fields when person type changes
      setEditingExpense(f => ({ ...f, [name]: value, employeeId: '', partyId: '', personName: '' }));
      setExpenseForm(f => ({ ...f, [name]: value, employeeId: '', partyId: '', personName: '' }));
      setNameSearchText('');
    } else if (name === 'employeeId') {
      const selectedEmp = employees.find(e => e.employeeId === value);
      setEditingExpense(f => ({ ...f, employeeId: value, personName: selectedEmp?.name || '' }));
      setExpenseForm(f => ({ ...f, employeeId: value, personName: selectedEmp?.name || '' }));
      setNameSearchText(selectedEmp ? `${selectedEmp.name} (${selectedEmp.employeeId})` : '');
      setShowNameDropdown(false);
    } else if (name === 'partyId') {
      const selectedParty = parties.find(p => p.id === value);
      setEditingExpense(f => ({ ...f, partyId: value, personName: selectedParty?.firmName || selectedParty?.name || '' }));
      setExpenseForm(f => ({ ...f, partyId: value, personName: selectedParty?.firmName || selectedParty?.name || '' }));
      setNameSearchText(selectedParty ? (selectedParty.firmName || selectedParty.name) : '');
      setShowNameDropdown(false);
    } else if (name === 'nameSearchText') {
      setNameSearchText(value);
      setShowNameDropdown(true);
    } else if (name === 'receipt') {
      if (e.target.clearReceipt) {
        setEditingExpense((f) => ({ ...f, receipt: null, receiptData: null }));
      } else {
        setEditingExpense((f) => ({ ...f, receipt: files?.length ? files[0] : null }));
      }
    } else {
      setEditingExpense(f => ({ ...f, [name]: files ? files[0] : value }));
      // Also update expense form to keep them in sync
      if (name !== 'receipt') {
        setExpenseForm(f => ({ ...f, [name]: value }));
      }
    }
  };
  
  // Handle filter changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleDateChange = (value) => {
    setDateFilter(value);
  };

  const handleAmountChange = (value) => {
    setAmountFilter(value);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setDateFilter('');
    setAmountFilter('');
  };

  // Close mobile filter dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileFilters && !event.target.closest('.mobile-filter-dropdown')) {
        setShowMobileFilters(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showMobileFilters) {
        setShowMobileFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMobileFilters]);
  const handleSaveExpense = async id => {
    // Handle document upload if a new file is selected
    let receiptData = editingExpense.receiptData ?? null;
    if (editingExpense.receipt === null && editingExpense.receiptData === null) {
      receiptData = null;
    } else if (editingExpense.receipt && editingExpense.receipt instanceof File) {
      const file = editingExpense.receipt;
      const storageRef = ref(storage, `receipts/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const receiptUrl = await getDownloadURL(storageRef);
      receiptData = {
        url: receiptUrl,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString()
      };
    }
    
    const updateData = {
      date: editingExpense.date || '',
      head: editingExpense.head || '',
      amount: Number(editingExpense.amount) || 0,
      description: editingExpense.description || '',
      remarks: editingExpense.remarks || '',
      month: editingExpense.month || '',
      paymentMode: editingExpense.paymentMode || 'Cash',
      constantEntry: editingExpense.constantEntry || '',
      receiptData: receiptData,
    };
    
    // Add person/name information if present in expense form
    if (expenseForm.personType === 'Employee' && expenseForm.employeeId) {
      updateData.personType = 'Employee';
      updateData.employeeId = expenseForm.employeeId;
      updateData.personName = expenseForm.personName;
    } else if (expenseForm.personType === 'Party' && expenseForm.partyId) {
      updateData.personType = 'Party';
      updateData.partyId = expenseForm.partyId;
      updateData.personName = expenseForm.personName;
    } else {
      // Clear name fields if not selected
      updateData.personType = 'None';
      updateData.employeeId = '';
      updateData.partyId = '';
      updateData.personName = '';
    }
    appendWorkOrderFields(updateData, editingExpense);

    await updateDoc(doc(getCollectionForCompany(db, { companyId, appId, userId }, 'expenses'), id), updateData);
    await syncLedgerFromSource(db, companyCtx, {
      sourceType: LEDGER_SOURCE_TYPES.expense,
      sourceCollection: 'expenses',
      sourceId: id,
      doc: { ...updateData, id },
    });
    
    // Show success notification
    success(
      `Expense of ${formatCurrency(updateData.amount, companyCurrency)} updated successfully!`,
      {
        title: 'Expense Updated',
        details: `${updateData.date} • ${updateData.head} • ${updateData.paymentMode || 'Cash'}`,
        duration: 6000
      }
    );
    
    await reloadExpenses();
    setEditingExpenseId(null);
    setEditingExpense({});
    setExpenseForm({ 
      date: new Date().toISOString().split('T')[0], 
      head: '', 
      amount: '', 
      description: '', 
      receipt: null,
      paymentMode: 'Cash',
      constantEntry: '',
      personType: 'None',
      employeeId: '',
      partyId: '',
      personName: ''
    });
    setNameSearchText('');
  };
  const handleCancelEditExpense = () => {
    setEditingExpenseId(null);
    setEditingExpense({});
    setExpenseForm({ 
      date: new Date().toISOString().split('T')[0], 
      head: '', 
      amount: '', 
      description: '', 
      receipt: null,
      paymentMode: 'Cash',
      constantEntry: '',
      personType: 'None',
      employeeId: '',
      partyId: '',
      personName: ''
    });
    setNameSearchText('');
    // Focus first input to make it clear we are back in add mode
    try { document.querySelector('input[name="date"]').focus(); } catch {}
  };

  // Expense handlers
  const handleViewExpense = (expense) => {
    info(`Expense Details:\nDate: ${expense.date}\nHead: ${expense.head}\nAmount: ${formatCurrency(Number(expense.amount), companyCurrency)}\nPayment Mode: ${expense.paymentMode || 'N/A'}\nDescription: ${expense.description || 'N/A'}`, { title: 'Expense Details', duration: 10000 });
  };



  const handleDeleteExpense = async id => {
    const expenseToDelete = expenses.find(e => e.id === id);
    const expenseAmount = expenseToDelete ? formatCurrency(Number(expenseToDelete.amount) || 0, companyCurrency) : '';
    const expenseHead = expenseToDelete?.head || 'Unknown';
    
    if (
      window.confirm(
        'Are you sure you want to delete this expense? Any linked employee wallet transaction will be reversed.'
      )
    ) {
      try {
        const companyData = { companyId, appId, userId };
        const cascadeResult = await EmployeeCashManager.deleteTransactionsForExpense(companyData, id);
        if (!cascadeResult.success && cascadeResult.error) {
          error(
            `Could not delete linked wallet transactions: ${cascadeResult.error}. Delete them from Employee Cash Flow Report first, then delete this expense.`,
            { title: 'Cascade Delete Failed', duration: 8000 }
          );
          return;
        }
        await removeLedgerEntry(db, companyCtx, {
          sourceType: LEDGER_SOURCE_TYPES.expense,
          sourceId: id,
        });
        await deleteDoc(doc(getCollectionForCompany(db, companyData, 'expenses'), id));
        const msg =
          cascadeResult.deletedCount > 0
            ? `Expense and ${cascadeResult.deletedCount} linked wallet transaction(s) deleted. Amounts reversed.`
            : 'Expense deleted successfully!';
        success(msg, {
          title: 'Expense Deleted',
          details: expenseAmount ? `${expenseAmount} • ${expenseHead}` : expenseHead,
          duration: 6000,
        });
        await reloadExpenses();
      } catch (err) {
        console.error('Error deleting expense:', err);
        error('Error deleting expense. Please try again.', { title: 'Delete Failed', duration: 7000 });
      }
    }
  };

  // --- Summary/Analytics ---
  const totalAmount = expenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
  const totalCount = expenses.length;

  // Category-wise analytics (by head/employeeName)
  const categorySummary = useMemo(() => {
    const map = {};
    for (const exp of expenses) {
      const key = exp.head || exp.employeeName || 'Other';
      if (!map[key]) map[key] = 0;
      map[key] += Number(exp.amount) || 0;
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

  // Date-wise analytics (by date/month)
  const dateSummary = useMemo(() => {
    const map = {};
    for (const exp of expenses) {
      const key = exp.date || exp.month || 'Other';
      if (!map[key]) map[key] = 0;
      map[key] += Number(exp.amount) || 0;
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [expenses]);

  // --- Filtered, sorted, paginated expenses for table ---
  const filteredExpenses = useMemo(() => {
    return expenses.filter(exp => {
      if (filters.date && exp.date !== filters.date) return false;
      if (filters.head && exp.head !== filters.head) return false;
      if (filters.amount && String(exp.amount) !== String(filters.amount)) return false;
      return true;
    });
  }, [expenses, filters]);

  const sortedExpenses = useMemo(() => {
    const arr = [...filteredExpenses];
    arr.sort((a, b) => {
      let aValue = a[sortKey] || '';
      let bValue = b[sortKey] || '';
      if (sortKey === 'amount') {
        aValue = Number(aValue);
        bValue = Number(bValue);
        return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sortKey === 'date' || sortKey === 'month') {
        return sortDir === 'asc' ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
      }
      return sortDir === 'asc' ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
    });
    return arr;
  }, [filteredExpenses, sortKey, sortDir]);

  const totalPages = Math.ceil(sortedExpenses.length / pageSize);
  const paginatedExpenses = sortedExpenses.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // --- Export to CSV ---
  const handleExportCSV = () => {
    let headers, rows, filename;
    
    if (selectedGroup === 'fixed' || selectedGroup === 'variable') {
      headers = ['Date', 'Category', 'Constant Entry', 'Amount', 'Payment Mode', 'Description'];
      rows = expenses.map(exp => [
        exp.date || '',
        exp.head || '',
        exp.constantEntry || '',
        exp.amount || '',
        exp.paymentMode || '',
        exp.description || ''
      ]);
      filename = `${selectedGroup}_expenses_export_${new Date().toISOString().slice(0,10)}.csv`;
    } else if (selectedGroup === 'advances') {
      headers = ['Date', 'Person Type', 'Person Name', 'Employee ID', 'Payment Type', 'Amount', 'Remark', 'Payment Mode'];
      rows = irregularPayments.map(payment => [
        payment.date || '',
        payment.personType || '',
        payment.personName || '',
        payment.employeeId || '',
        payment.paymentType || '',
        payment.amount || '',
        payment.remark || '',
        payment.paymentMode || ''
      ]);
      filename = `labour_advances_export_${new Date().toISOString().slice(0,10)}.csv`;
    } else {
      // Default fallback
      headers = ['Date/Month', 'Head/Employee', 'Amount', 'Description/Remarks'];
      rows = expenses.map(exp => [
      exp.date || exp.month || '',
      exp.head || exp.employeeName || '',
      exp.amount || '',
        exp.description || exp.remarks || ''
    ]);
      filename = `expenses_export_${selectedGroup}_${new Date().toISOString().slice(0,10)}.csv`;
    }
    
    // Get company details for letterhead
    const companyName = currentCompany?.details?.firmName || 
                       currentCompany?.details?.companyName || 
                       currentCompany?.details?.name || 
                       currentCompany?.details?.businessName || 
                       currentCompany?.details?.company ||
                       currentCompany?.firmName || 
                       currentCompany?.companyName || 
                       currentCompany?.name || 
                       currentCompany?.businessName || 
                       currentCompany?.company ||
                       'Company Name';
    
    const gstin = currentCompany?.details?.gstin || currentCompany?.details?.gstNumber || currentCompany?.details?.gst || currentCompany?.details?.gstId ||
                  currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.gst || currentCompany?.gstId;
    const email = currentCompany?.details?.email || currentCompany?.details?.emailAddress || currentCompany?.details?.emailId ||
                  currentCompany?.email || currentCompany?.emailAddress || currentCompany?.emailId;
    const phone = currentCompany?.details?.phone || currentCompany?.details?.phoneNumber || currentCompany?.details?.mobile || currentCompany?.details?.contactNumber ||
                  currentCompany?.phone || currentCompany?.phoneNumber || currentCompany?.mobile || currentCompany?.contactNumber;
    const address = currentCompany?.details?.address || currentCompany?.details?.businessAddress || currentCompany?.details?.officeAddress || currentCompany?.details?.fullAddress ||
                   currentCompany?.address || currentCompany?.businessAddress || currentCompany?.officeAddress || currentCompany?.fullAddress;
    const city = currentCompany?.details?.city || currentCompany?.details?.cityName || currentCompany?.city || currentCompany?.cityName;
    const state = currentCompany?.details?.state || currentCompany?.details?.stateName || currentCompany?.state || currentCompany?.stateName;
    const pincode = currentCompany?.details?.pincode || currentCompany?.details?.pinCode || currentCompany?.details?.postalCode || 
                    currentCompany?.pincode || currentCompany?.pinCode || currentCompany?.postalCode;
    
    // Create letterhead
    let csvContent = '';
    csvContent += `"${companyName}"\n`;
    csvContent += '""\n'; // Empty row
    
    // Add contact details row
    const contactDetails = [];
    if (gstin) contactDetails.push(`GSTIN: ${gstin}`);
    if (email) contactDetails.push(`Email: ${email}`);
    if (phone) contactDetails.push(`Phone: ${phone}`);
    if (contactDetails.length > 0) {
      csvContent += `"${contactDetails.join(' | ')}"\n`;
    }
    
    // Add address row
    const addressParts = [];
    if (address) addressParts.push(address);
    if (city && state) {
      addressParts.push(`${city}, ${state}${pincode ? ` - ${pincode}` : ''}`);
    }
    if (addressParts.length > 0) {
      csvContent += `"${addressParts.join(', ')}"\n`;
    }
    
    // Add report title and date
    const reportTitle = selectedGroup === 'advances' ? 'Labour/Advances Report' : 
                       selectedGroup === 'fixed' ? 'Fixed Expenses Report' :
                       selectedGroup === 'variable' ? 'Variable Expenses Report' : 'Expenses Report';
    
    csvContent += '""\n'; // Empty row
    csvContent += `"${reportTitle}"\n`;
    csvContent += `"Generated on: ${new Date().toLocaleDateString('en-IN')}"\n`;
    csvContent += '""\n'; // Empty row
    csvContent += '""\n'; // Empty row before data
    
    // Add data
    csvContent += headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(',') + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportDropdownOpen(false);
  };

  // --- Export to Excel (XLSX) ---
  const handleExportExcel = () => {
    let headers, rows, filename;
    
    if (selectedGroup === 'fixed' || selectedGroup === 'variable') {
      headers = ['Date', 'Category', 'Constant Entry', 'Amount', 'Payment Mode', 'Description'];
      rows = expenses.map(exp => [
        exp.date || '',
        exp.head || '',
        exp.constantEntry || '',
        exp.amount || '',
        exp.paymentMode || '',
        exp.description || ''
      ]);
      filename = `${selectedGroup}_expenses_export_${new Date().toISOString().slice(0,10)}.xlsx`;
    } else if (selectedGroup === 'advances') {
      headers = ['Date', 'Person Type', 'Person Name', 'Employee ID', 'Payment Type', 'Amount', 'Remark', 'Payment Mode'];
      rows = irregularPayments.map(payment => [
        payment.date || '',
        payment.personType || '',
        payment.personName || '',
        payment.employeeId || '',
        payment.paymentType || '',
        payment.amount || '',
        payment.remark || '',
        payment.paymentMode || ''
      ]);
      filename = `labour_advances_export_${new Date().toISOString().slice(0,10)}.xlsx`;
    } else {
      // Default fallback
      headers = ['Date/Month', 'Head/Employee', 'Amount', 'Description/Remarks'];
      rows = expenses.map(exp => [
      exp.date || exp.month || '',
      exp.head || exp.employeeName || '',
      exp.amount || '',
        exp.description || exp.remarks || ''
    ]);
      filename = `expenses_export_${selectedGroup}_${new Date().toISOString().slice(0,10)}.xlsx`;
    }
    
    // Get company details for letterhead
    const companyName = currentCompany?.details?.firmName || 
                       currentCompany?.details?.companyName || 
                       currentCompany?.details?.name || 
                       currentCompany?.details?.businessName || 
                       currentCompany?.details?.company ||
                       currentCompany?.firmName || 
                       currentCompany?.companyName || 
                       currentCompany?.name || 
                       currentCompany?.businessName || 
                       currentCompany?.company ||
                       'Company Name';
    
    const gstin = currentCompany?.details?.gstin || currentCompany?.details?.gstNumber || currentCompany?.details?.gst || currentCompany?.details?.gstId ||
                  currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.gst || currentCompany?.gstId;
    const email = currentCompany?.details?.email || currentCompany?.details?.emailAddress || currentCompany?.details?.emailId ||
                  currentCompany?.email || currentCompany?.emailAddress || currentCompany?.emailId;
    const phone = currentCompany?.details?.phone || currentCompany?.details?.phoneNumber || currentCompany?.details?.mobile || currentCompany?.details?.contactNumber ||
                  currentCompany?.phone || currentCompany?.phoneNumber || currentCompany?.mobile || currentCompany?.contactNumber;
    const address = currentCompany?.details?.address || currentCompany?.details?.businessAddress || currentCompany?.details?.officeAddress || currentCompany?.details?.fullAddress ||
                   currentCompany?.address || currentCompany?.businessAddress || currentCompany?.officeAddress || currentCompany?.fullAddress;
    const city = currentCompany?.details?.city || currentCompany?.details?.cityName || currentCompany?.city || currentCompany?.cityName;
    const state = currentCompany?.details?.state || currentCompany?.details?.stateName || currentCompany?.state || currentCompany?.stateName;
    const pincode = currentCompany?.details?.pincode || currentCompany?.details?.pinCode || currentCompany?.details?.postalCode || 
                    currentCompany?.pincode || currentCompany?.pinCode || currentCompany?.postalCode;
    
    // Create letterhead rows
    const letterheadRows = [
      [companyName],
      [''], // Empty row
    ];
    
    // Add contact details row
    const contactDetails = [];
    if (gstin) contactDetails.push(`GSTIN: ${gstin}`);
    if (email) contactDetails.push(`Email: ${email}`);
    if (phone) contactDetails.push(`Phone: ${phone}`);
    if (contactDetails.length > 0) {
      letterheadRows.push([contactDetails.join(' | ')]);
    }
    
    // Add address row
    const addressParts = [];
    if (address) addressParts.push(address);
    if (city && state) {
      addressParts.push(`${city}, ${state}${pincode ? ` - ${pincode}` : ''}`);
    }
    if (addressParts.length > 0) {
      letterheadRows.push([addressParts.join(', ')]);
    }
    
    // Add report title and date
    const reportTitle = selectedGroup === 'advances' ? 'Labour/Advances Report' : 
                       selectedGroup === 'fixed' ? 'Fixed Expenses Report' :
                       selectedGroup === 'variable' ? 'Variable Expenses Report' : 'Expenses Report';
    
    letterheadRows.push(
      [''],
      [reportTitle],
      [`Generated on: ${new Date().toLocaleDateString('en-IN')}`],
      [''],
      [''] // Empty row before data
    );
    
    // Combine letterhead with data
    const allRows = [...letterheadRows, headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(allRows);
    const wb = XLSX.utils.book_new();
    
    // Create clean sheet name based on selected group
    let sheetName = 'Expenses';
    if (selectedGroup === 'advances') {
      sheetName = 'Labour Advances';
    } else if (selectedGroup === 'fixed') {
      sheetName = 'Fixed Expenses';
    } else if (selectedGroup === 'variable') {
      sheetName = 'Variable Expenses';
    } else if (selectedGroup === 'salaries') {
      sheetName = 'Salary Expenses';
    }

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
    setExportDropdownOpen(false);
  };

  // --- Export to PDF (table only) ---
  const handleExportPDF = () => {
    const docPDF = new jsPDF();
    let headers, rows, filename, reportTitle;
    
    if (selectedGroup === 'fixed' || selectedGroup === 'variable') {
      headers = [['Date', 'Category', 'Constant Entry', 'Amount', 'Payment Mode', 'Description']];
      rows = expenses.map(exp => [
        exp.date || '',
        exp.head || '',
        exp.constantEntry || '',
        exp.amount || '',
        exp.paymentMode || '',
        exp.description || ''
      ]);
      filename = `${selectedGroup}_expenses_export_${new Date().toISOString().slice(0,10)}.pdf`;
      reportTitle = `${selectedGroup.charAt(0).toUpperCase() + selectedGroup.slice(1)} Expenses Report`;
    } else if (selectedGroup === 'advances') {
      headers = [['Date', 'Person Type', 'Person Name', 'Employee ID', 'Payment Type', 'Amount', 'Remark', 'Payment Mode']];
      rows = irregularPayments.map(payment => [
        payment.date || '',
        payment.personType || '',
        payment.personName || '',
        payment.employeeId || '',
        payment.paymentType || '',
        payment.amount || '',
        payment.remark || '',
        payment.paymentMode || ''
      ]);
      filename = `labour_advances_export_${new Date().toISOString().slice(0,10)}.pdf`;
      reportTitle = 'Labour & Advances Report';
    } else {
      // Default fallback
      headers = [['Date/Month', 'Head/Employee', 'Amount', 'Description/Remarks']];
      rows = expenses.map(exp => [
      exp.date || exp.month || '',
      exp.head || exp.employeeName || '',
      exp.amount || '',
        exp.description || exp.remarks || ''
      ]);
      filename = `expenses_export_${selectedGroup}_${new Date().toISOString().slice(0,10)}.pdf`;
      reportTitle = 'Expenses Report';
    }
    
    // Add company letterhead with compact spacing and center alignment
    docPDF.setFontSize(18);
    docPDF.setFont('helvetica', 'bold');
    
    // Debug company data
    console.log('Company data for PDF:', currentCompany);
    console.log('Available keys in currentCompany:', currentCompany ? Object.keys(currentCompany) : 'No currentCompany');
    
    // Check multiple possible field names for company name - including nested details
    const companyName = currentCompany?.details?.firmName || 
                       currentCompany?.details?.companyName || 
                       currentCompany?.details?.name || 
                       currentCompany?.details?.businessName || 
                       currentCompany?.details?.company ||
                       currentCompany?.firmName || 
                       currentCompany?.companyName || 
                       currentCompany?.name || 
                       currentCompany?.businessName || 
                       currentCompany?.company ||
                       'Company Name';
    
    console.log('Selected company name:', companyName);
    console.log('Details object:', currentCompany?.details);
    
    // Center align company name
    const pageWidth = docPDF.internal.pageSize.width;
    const companyNameWidth = docPDF.getTextWidth(companyName);
    docPDF.text(companyName, (pageWidth - companyNameWidth) / 2, 20);
    
    docPDF.setFontSize(9);
    docPDF.setFont('helvetica', 'normal');
    let yPos = 26;
    
    // Get company details
    const gstin = currentCompany?.details?.gstin || currentCompany?.details?.gstNumber || currentCompany?.details?.gst || currentCompany?.details?.gstId ||
                  currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.gst || currentCompany?.gstId;
    const email = currentCompany?.details?.email || currentCompany?.details?.emailAddress || currentCompany?.details?.emailId ||
                  currentCompany?.email || currentCompany?.emailAddress || currentCompany?.emailId;
    const phone = currentCompany?.details?.phone || currentCompany?.details?.phoneNumber || currentCompany?.details?.mobile || currentCompany?.details?.contactNumber ||
                  currentCompany?.phone || currentCompany?.phoneNumber || currentCompany?.mobile || currentCompany?.contactNumber;
    const address = currentCompany?.details?.address || currentCompany?.details?.businessAddress || currentCompany?.details?.officeAddress || currentCompany?.details?.fullAddress ||
                   currentCompany?.address || currentCompany?.businessAddress || currentCompany?.officeAddress || currentCompany?.fullAddress;
    const city = currentCompany?.details?.city || currentCompany?.details?.cityName || currentCompany?.city || currentCompany?.cityName;
    const state = currentCompany?.details?.state || currentCompany?.details?.stateName || currentCompany?.state || currentCompany?.stateName;
    const pincode = currentCompany?.details?.pincode || currentCompany?.details?.pinCode || currentCompany?.details?.postalCode || 
                    currentCompany?.pincode || currentCompany?.pinCode || currentCompany?.postalCode;
    
    // Create contact details row (GSTIN, Email, Phone)
    const contactDetails = [];
    if (gstin) contactDetails.push(`GSTIN: ${gstin}`);
    if (email) contactDetails.push(`Email: ${email}`);
    if (phone) contactDetails.push(`Phone: ${phone}`);
    
    if (contactDetails.length > 0) {
      const contactText = contactDetails.join(' | ');
      const contactWidth = docPDF.getTextWidth(contactText);
      docPDF.text(contactText, (pageWidth - contactWidth) / 2, yPos);
      yPos += 4;
    }
    
    // Create address row
    const addressParts = [];
    if (address) addressParts.push(address);
    if (city && state) {
      addressParts.push(`${city}, ${state}${pincode ? ` - ${pincode}` : ''}`);
    }
    
    if (addressParts.length > 0) {
      const addressText = addressParts.join(', ');
      const addressWidth = docPDF.getTextWidth(addressText);
      docPDF.text(addressText, (pageWidth - addressWidth) / 2, yPos);
      yPos += 4;
    }
    
    // Add line separator
    docPDF.line(20, yPos + 2, 190, yPos + 2);
    
    // Add report title
    docPDF.setFontSize(14);
    docPDF.setFont('helvetica', 'bold');
    docPDF.text(reportTitle, 20, yPos + 8);
    
    // Add report date
    docPDF.setFontSize(9);
    docPDF.setFont('helvetica', 'normal');
    docPDF.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 20, yPos + 14);
    
    // Add line separator
    docPDF.line(20, yPos + 17, 190, yPos + 17);
    
    import('jspdf-autotable')
      .then(({ default: autoTable }) => {
        autoTable(docPDF, { 
          head: headers, 
          body: rows,
          startY: yPos + 20,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [59, 130, 246] }
        });
        
        // Add software tagline at bottom
        const finalY = docPDF.lastAutoTable.finalY || 200;
        docPDF.setFontSize(8);
        docPDF.setFont('helvetica', 'italic');
        docPDF.text('AccToo', 20, finalY + 15);
        docPDF.text('"Simplifying Accounting. Together"', 20, finalY + 20);
        
        docPDF.save(filename);
        setExportDropdownOpen(false);
      })
      .catch((e) => {
        console.warn('jspdf-autotable not available in Expenses export:', e);
        // Fallback: save without table
        docPDF.text('PDF Export Error: Table library not available', 20, 85);
        docPDF.text('AccToo', 20, 100);
        docPDF.text('"Simplifying Accounting. Together"', 20, 105);
        docPDF.save(filename);
    setExportDropdownOpen(false);
      });
  };

  // --- Close dropdown on outside click ---
  useEffect(() => {
    function handleClickOutside(event) {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
      // Close employee dropdown when clicking outside
      if (showEmployeeDropdown) {
        setShowEmployeeDropdown(false);
        setSelectedEmployeeIndex(-1);
      }
    }
    if (exportDropdownOpen || showEmployeeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportDropdownOpen, showEmployeeDropdown]);




  const renderGroupForm = () => {
    if (selectedGroup === 'employee') {
      return <EmployeeManagementRedirect />;
    }
    if (selectedGroup === 'salaries') {
      const totalPaid = salaryPayments.reduce(
        (s, p) => s + (Number(p.netAmount || p.total) || 0),
        0
      );
      const employeesNeedingMigration = (contextEmployees || []).filter(employeeNeedsSalaryMigration);

      return (
        <SalaryProcessingTab
          t={t}
          companyCurrency={companyCurrency}
          currentCompany={currentCompany}
          employees={employees}
          contextEmployees={contextEmployees}
          salaryPayments={salaryPayments}
          irregularPaymentsForEmployee={irregularPaymentsForEmployee}
          selectedEmp={selectedEmp}
          totalEarnings={totalEarnings}
          totalDeductions={totalDeductions}
          netSalary={netSalary}
          totalPaid={totalPaid}
          employeesNeedingMigration={employeesNeedingMigration}
          salaryError={salaryError}
          salaryEmployeeId={salaryEmployeeId}
          salaryDate={salaryDate}
          salaryMonth={salaryMonth}
          salaryPaymentMode={salaryPaymentMode}
          fixedSalaryRows={fixedSalaryRows}
          performanceSalaryRows={performanceSalaryRows}
          deductionRows={deductionRows}
          adjustSalaryFromBalance={adjustSalaryFromBalance}
          salaryBalanceSource={salaryBalanceSource}
          salaryBalanceAmount={salaryBalanceAmount}
          adjustSalaryFromOtherWallets={adjustSalaryFromOtherWallets}
          salarySourceEmployeeId={salarySourceEmployeeId}
          salarySourceEmployeeBalance={salarySourceEmployeeBalance}
          salaryOtherWalletsAmount={salaryOtherWalletsAmount}
          salaryBalanceSourceOther={salaryBalanceSourceOther}
          appliedIrregularSelections={appliedIrregularSelections}
          showEmployeeDropdown={showEmployeeDropdown}
          salarySearch={salarySearch}
          selectedEmployeeDisplay={selectedEmployeeDisplay}
          selectedEmployeeIndex={selectedEmployeeIndex}
          filteredEmployeeOptions={filteredEmployeeOptions}
          expandedSalaryCards={expandedSalaryCards}
          setSalaryError={setSalaryError}
          setSalaryEmployeeId={setSalaryEmployeeId}
          setSalaryDate={setSalaryDate}
          setSalaryMonth={setSalaryMonth}
          setSalaryPaymentMode={setSalaryPaymentMode}
          setFixedSalaryRows={setFixedSalaryRows}
          setPerformanceSalaryRows={setPerformanceSalaryRows}
          setSalaryDeductions={setSalaryDeductions}
          setAdjustSalaryFromBalance={setAdjustSalaryFromBalance}
          setSalaryBalanceSource={setSalaryBalanceSource}
          setSalaryBalanceAmount={setSalaryBalanceAmount}
          setAdjustSalaryFromOtherWallets={setAdjustSalaryFromOtherWallets}
          setSalarySourceEmployeeId={setSalarySourceEmployeeId}
          resetOtherWalletSettlement={resetOtherWalletSettlement}
          setSalaryOtherWalletsAmount={setSalaryOtherWalletsAmount}
          setSalaryBalanceSourceOther={setSalaryBalanceSourceOther}
          setAppliedIrregularSelections={setAppliedIrregularSelections}
          setSalarySearch={setSalarySearch}
          setSelectedEmployeeDisplay={setSelectedEmployeeDisplay}
          setSelectedEmployeeIndex={setSelectedEmployeeIndex}
          setExpandedSalaryCards={setExpandedSalaryCards}
          handleDropdownToggle={handleDropdownToggle}
          handleEmployeeKeyDown={handleEmployeeKeyDown}
          handleEmployeeSelect={handleEmployeeSelect}
          handleFixedSalaryRowChange={handleFixedSalaryRowChange}
          addFixedSalaryRow={addFixedSalaryRow}
          removeFixedSalaryRow={removeFixedSalaryRow}
          handlePerformanceRowChange={handlePerformanceRowChange}
          addPerformanceRow={addPerformanceRow}
          removePerformanceRow={removePerformanceRow}
          handleDeductionRowChange={handleDeductionRowChange}
          addDeductionRow={addDeductionRow}
          removeDeductionRow={removeDeductionRow}
          handleIrregularToggle={handleIrregularToggle}
          handleIrregularAppliedAsChange={handleIrregularAppliedAsChange}
          handleSalarySubmit={handleSalarySubmit}
          handleViewSalarySlip={handleViewSalarySlip}
          handleEditSalaryPayment={handleEditSalaryPayment}
          handleDeleteSalaryPayment={handleDeleteSalaryPayment}
          handleExportSalaryCSV={handleExportSalaryCSV}
          handleExportSalaryExcel={handleExportSalaryExcel}
          handleExportSalaryPDF={handleExportSalaryPDF}
          info={info}
        />
      );
    }
    if (selectedGroup === 'fixed') {
      return (
        <GroupedExpensesTab
          variant="fixed"
          companyCurrency={companyCurrency}
          categoryHeads={FIXED_HEADS}
          constantEntries={CONSTANT_ENTRIES}
          expenseForm={expenseForm}
          editingExpenseId={editingExpenseId}
          editingExpense={editingExpense}
          handleExpenseFormChange={handleExpenseFormChange}
          handleEditingExpenseChange={handleEditingExpenseChange}
          handleAddExpense={handleAddExpense}
          handleSaveExpense={handleSaveExpense}
          handleCancelEditExpense={handleCancelEditExpense}
          tableFilteredExpenses={tableFilteredExpenses}
          totalAmount={totalAmount}
          searchQuery={searchQuery}
          dateFilter={dateFilter}
          amountFilter={amountFilter}
          handleSearchChange={handleSearchChange}
          handleDateChange={handleDateChange}
          handleAmountChange={handleAmountChange}
          clearAllFilters={clearAllFilters}
          tablePagination={tablePagination}
          sortConfig={sortConfig}
          tableHandleSort={tableHandleSort}
          showMobileFilters={showMobileFilters}
          setShowMobileFilters={setShowMobileFilters}
          isCardExpanded={isCardExpanded}
          toggleCard={toggleCard}
          handleEditExpense={handleEditExpense}
          handleDeleteExpense={handleDeleteExpense}
          setExpenseForm={setExpenseForm}
          setEditingExpense={setEditingExpense}
          employees={employees}
          onSettleFromWallet={handleOpenSettleExpenseFromWallet}
          settleExpenseModal={settleExpenseModal}
          setSettleExpenseModal={setSettleExpenseModal}
          settleExpenseDate={settleExpenseDate}
          setSettleExpenseDate={setSettleExpenseDate}
          settleExpensePaymentMode={settleExpensePaymentMode}
          setSettleExpensePaymentMode={setSettleExpensePaymentMode}
          settleExpenseEmployeeId={settleExpenseEmployeeId}
          setSettleExpenseEmployeeId={setSettleExpenseEmployeeId}
          settleExpenseEmployeeBalance={settleExpenseEmployeeBalance}
          settleExpenseRemark={settleExpenseRemark}
          setSettleExpenseRemark={setSettleExpenseRemark}
          settleExpenseSubmitting={settleExpenseSubmitting}
          handleConfirmSettleExpenseFromWallet={handleConfirmSettleExpenseFromWallet}
          recordSettleFromWallet={recordSettleFromWalletExpense}
          setRecordSettleFromWallet={setRecordSettleFromWalletExpense}
          selectedEmployeeForCashFlow={selectedEmployeeForCashFlowExpense}
          setSelectedEmployeeForCashFlow={setSelectedEmployeeForCashFlowExpense}
          expenseSourceEmployeeBalance={expenseSourceEmployeeBalance}
        />
      );
    }
    if (selectedGroup === 'advances') {
      return (
        <LabourAdvancesTab
          companyCurrency={companyCurrency}
          companyCtx={companyCtx}
          db={db}
          employees={employees}
          irregularForm={irregularForm}
          setIrregularForm={setIrregularForm}
          handleIrregularFormChange={handleIrregularFormChange}
          handleSaveIrregular={handleSaveIrregular}
          handleIrregularEmployeeSelect={(e) => setIrregularForm((f) => ({ ...f, employeeId: e.target.value }))}
          showIrregularNameDropdown={showIrregularNameDropdown}
          setShowIrregularNameDropdown={setShowIrregularNameDropdown}
          filteredIrregularNames={filteredIrregularNames}
          handleIrregularNameSelect={handleIrregularNameSelect}
          irregularNameDropdownRef={irregularNameDropdownRef}
          recordInEmployeeCashFlowLabour={recordInEmployeeCashFlowLabour}
          setRecordInEmployeeCashFlowLabour={setRecordInEmployeeCashFlowLabour}
          selectedEmployeeForCashFlowLabour={selectedEmployeeForCashFlowLabour}
          setSelectedEmployeeForCashFlowLabour={setSelectedEmployeeForCashFlowLabour}
          markLabourSettledFromWallet={markLabourSettledFromWallet}
          setMarkLabourSettledFromWallet={setMarkLabourSettledFromWallet}
          labourSourceEmployeeBalance={labourSourceEmployeeBalance}
          rows={labourAdvanceFilteredSortedRows}
          labourAdvanceFilter={labourAdvanceFilter}
          setLabourAdvanceFilter={setLabourAdvanceFilter}
          labourAdvanceSort={labourAdvanceSort}
          setLabourAdvanceSort={setLabourAdvanceSort}
          expandedCards={expandedCards}
          toggleCard={toggleCard}
          handleDeleteIrregular={handleDeleteIrregular}
          handleOpenSettleLabourFromWallet={handleOpenSettleLabourFromWallet}
          settleLabourModalPayment={settleLabourModalPayment}
          setSettleLabourModalPayment={setSettleLabourModalPayment}
          settleLabourDate={settleLabourDate}
          setSettleLabourDate={setSettleLabourDate}
          settleLabourPaymentMode={settleLabourPaymentMode}
          setSettleLabourPaymentMode={setSettleLabourPaymentMode}
          settleLabourEmployeeId={settleLabourEmployeeId}
          setSettleLabourEmployeeId={setSettleLabourEmployeeId}
          settleLabourEmployeeBalance={settleLabourEmployeeBalance}
          settleLabourRemark={settleLabourRemark}
          setSettleLabourRemark={setSettleLabourRemark}
          settleLabourSubmitting={settleLabourSubmitting}
          handleConfirmSettleLabourFromWallet={handleConfirmSettleLabourFromWallet}
        />
      );
    }

    if (selectedGroup === 'variable') {
      return (
        <GroupedExpensesTab
          variant="variable"
          companyCurrency={companyCurrency}
          companyCtx={companyCtx}
          db={db}
          categoryHeads={VARIABLE_HEADS}
          constantEntries={CONSTANT_ENTRIES}
          expenseForm={expenseForm}
          editingExpenseId={editingExpenseId}
          editingExpense={editingExpense}
          handleExpenseFormChange={handleExpenseFormChange}
          handleEditingExpenseChange={handleEditingExpenseChange}
          handleAddExpense={handleAddExpense}
          handleSaveExpense={handleSaveExpense}
          handleCancelEditExpense={handleCancelEditExpense}
          tableFilteredExpenses={tableFilteredExpenses}
          totalAmount={totalAmount}
          searchQuery={searchQuery}
          dateFilter={dateFilter}
          amountFilter={amountFilter}
          handleSearchChange={handleSearchChange}
          handleDateChange={handleDateChange}
          handleAmountChange={handleAmountChange}
          clearAllFilters={clearAllFilters}
          tablePagination={tablePagination}
          sortConfig={sortConfig}
          tableHandleSort={tableHandleSort}
          showMobileFilters={showMobileFilters}
          setShowMobileFilters={setShowMobileFilters}
          isCardExpanded={isCardExpanded}
          toggleCard={toggleCard}
          handleEditExpense={handleEditExpense}
          handleDeleteExpense={handleDeleteExpense}
          setExpenseForm={setExpenseForm}
          setEditingExpense={setEditingExpense}
          showPersonVendor
          filteredNames={filteredNames}
          showNameDropdown={showNameDropdown}
          setShowNameDropdown={setShowNameDropdown}
          nameDropdownRef={nameDropdownRef}
          nameSearchText={nameSearchText}
          handleNameSelect={handleNameSelect}
          employees={employees}
          onSettleFromWallet={handleOpenSettleExpenseFromWallet}
          settleExpenseModal={settleExpenseModal}
          setSettleExpenseModal={setSettleExpenseModal}
          settleExpenseDate={settleExpenseDate}
          setSettleExpenseDate={setSettleExpenseDate}
          settleExpensePaymentMode={settleExpensePaymentMode}
          setSettleExpensePaymentMode={setSettleExpensePaymentMode}
          settleExpenseEmployeeId={settleExpenseEmployeeId}
          setSettleExpenseEmployeeId={setSettleExpenseEmployeeId}
          settleExpenseEmployeeBalance={settleExpenseEmployeeBalance}
          settleExpenseRemark={settleExpenseRemark}
          setSettleExpenseRemark={setSettleExpenseRemark}
          settleExpenseSubmitting={settleExpenseSubmitting}
          handleConfirmSettleExpenseFromWallet={handleConfirmSettleExpenseFromWallet}
          recordSettleFromWallet={recordSettleFromWalletExpense}
          setRecordSettleFromWallet={setRecordSettleFromWalletExpense}
          selectedEmployeeForCashFlow={selectedEmployeeForCashFlowExpense}
          setSelectedEmployeeForCashFlow={setSelectedEmployeeForCashFlowExpense}
          expenseSourceEmployeeBalance={expenseSourceEmployeeBalance}
        />
      );
    }
    return null;
  };


  // --- Pagination controls ---
  const handlePageChange = newPage => {
    setPage(newPage);
  };
  const handlePageSizeChange = e => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  // --- Table header with sorting ---
  const renderSortableHeader = (label, key) => (
    <th
      className="px-4 py-2 text-center cursor-pointer select-none hover:text-blue-600"
      onClick={() => handleSort(key)}
    >
      {label} {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '↕️'}
    </th>
  );

  // 1. Default present date for salaryDate
  useEffect(() => {
    if (!salaryDate) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      setSalaryDate(`${yyyy}-${mm}-${dd}`);
    }
  }, []);

  // 2. useEffect for employee selection
  useEffect(() => {
    if (salaryEmployeeId) {
      const emp = employees.find(e => e.employeeId === salaryEmployeeId);
      if (emp) {
        setFixedSalaryRows(getMonthlySalaryRows(emp));
        // Don't automatically clear performance rows - let user manage them manually
      }
    }
    // eslint-disable-next-line
  }, [salaryEmployeeId, isEditingSalary]);

  // 3. +Add Type only adds extra rows (removable)
  const addSalaryRow = () => setPerformanceSalaryRows(rows => [...rows, { type: '', amount: 0, remark: '', fixed: false }]);

  // Salary payment handlers (moved to main scope for edit modal access)
  const addPerformanceRow = () => setPerformanceSalaryRows(rows => [...rows, { type: '', amount: '', remark: '', fixed: false, removable: true }]);
  const removePerformanceRow = idx => setPerformanceSalaryRows(rows => rows.filter((_, i) => i !== idx));
  const handlePerformanceRowChange = (idx, field, value) => setPerformanceSalaryRows(rows => rows.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  
  const addDeduction = () => setSalaryDeductions(deductions => [...deductions, { reason: '', amount: '', remark: '', otherReason: '' }]);
  const removeDeduction = (idx) => setSalaryDeductions(deductions => deductions.filter((_, i) => i !== idx));
  const handleDeductionChange = (idx, field, value) => setSalaryDeductions(deductions => deductions.map((deduction, i) => i === idx ? { ...deduction, [field]: value } : deduction));

  // Edit fixed salary components (type, amount, remark) when editing an existing salary payment
  const handleFixedSalaryRowChange = (idx, field, value) => setFixedSalaryRows(rows => rows.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  const addFixedSalaryRow = () => setFixedSalaryRows(rows => [...rows, { type: '', amount: '', remark: 'Fixed Component', fixed: true, removable: true }]);
  const removeFixedSalaryRow = (idx) => {
    const row = fixedSalaryRows[idx];
    if (row?.removable) setFixedSalaryRows(rows => rows.filter((_, i) => i !== idx));
  };
  
  // Aliases for new 4-column layout
  const addDeductionRow = addDeduction;
  const removeDeductionRow = removeDeduction;
  const handleDeductionRowChange = handleDeductionChange;

  // Edit and Delete handlers for salary payments
  const handleEditSalaryPayment = (payment) => {
    setIsEditingSalary(true); // Set editing flag
    setEditingSalaryPayment(payment);
    setSalaryEmployeeId(payment.employeeId);
    setSalaryDate(payment.date);
    setSalaryMonth(payment.month);
    setSalaryPaymentMode(payment.paymentMode || 'Cash');
    setSelectedEmployeeDisplay(payment.employeeName);
    
    // Load existing data - handle both old and new formats
    if (payment.fixedRows && payment.performanceRows) {
      // New format
      setFixedSalaryRows(payment.fixedRows || []);
      setPerformanceSalaryRows(payment.performanceRows || []);
    } else if (payment.rows) {
      // Old format - separate fixed and performance components
      const fixedRows = payment.rows.filter(row => row.fixed);
      const performanceRows = payment.rows.filter(row => !row.fixed);
      setFixedSalaryRows(fixedRows);
      setPerformanceSalaryRows(performanceRows);
    } else {
      setFixedSalaryRows([]);
      setPerformanceSalaryRows([]);
    }
    setSalaryDeductions(payment.deductions || []);
    
    setShowEditSalaryModal(true);
  };

  // --- Irregular payments helpers ---
  const handleIrregularFormChange = e => {
    const { name, value } = e.target;
    setIrregularForm(f => ({ ...f, [name]: value }));
    
    // Show dropdown when typing in personName field
    if (name === 'personName' && value && irregularForm.personType !== 'Employee') {
      setShowIrregularNameDropdown(true);
    } else if (name === 'personType') {
      setShowIrregularNameDropdown(false);
    }
  };
  
  // Handle person name selection from dropdown for irregular payments
  const handleIrregularNameSelect = (name) => {
    setIrregularForm(f => ({ ...f, personName: name }));
    setShowIrregularNameDropdown(false);
  };
  
  // Close irregular name dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showIrregularNameDropdown && irregularNameDropdownRef.current && !irregularNameDropdownRef.current.contains(event.target)) {
        setShowIrregularNameDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showIrregularNameDropdown]);
  const handleSaveIrregular = async (e) => {
    e.preventDefault();
    if (!irregularForm.amount || Number(irregularForm.amount) <= 0) {
      warning('Enter valid amount');
      return;
    }
    if (irregularForm.personType === 'Employee' && !irregularForm.employeeId) {
      warning('Select employee');
      return;
    }
    const companyData = { companyId, appId, userId };
    const personName = irregularForm.personType === 'Employee'
      ? (employees.find(emp => emp.employeeId === irregularForm.employeeId)?.name || 'Employee')
      : irregularForm.personName;
    const amountNum = Number(irregularForm.amount);

    if (recordInEmployeeCashFlowLabour && selectedEmployeeForCashFlowLabour) {
      const selectedWalletEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlowLabour);
      if (!selectedWalletEmployee || !isEmployeeActive(selectedWalletEmployee)) {
        warning('Selected employee is inactive. Please choose an active employee wallet.', {
          title: 'Inactive Employee',
          duration: 5000
        });
        return;
      }
      const paymentModeRaw = (irregularForm.paymentMode || 'Cash').toLowerCase();
      const paymentMode = paymentModeRaw === 'upi' ? 'upi' : 'cash';
      const balance = labourSourceEmployeeBalance || { cash: 0, upi: 0, total: 0 };
      const available = paymentMode === 'cash' ? balance.cash : paymentMode === 'upi' ? balance.upi : balance.total;
      if (available < amountNum) {
        warning(`Insufficient wallet balance. Available: ₹${(available || 0).toLocaleString('en-IN')}`, { title: 'Employee wallet', duration: 5000 });
        return;
      }
    }

    try {
      const irregularPayload = {
        ...irregularForm,
        amount: amountNum,
        applied: false,
        createdAt: serverTimestamp(),
      };
      appendWorkOrderFields(irregularPayload, irregularForm);
      const ref = await addDoc(getCollectionForCompany(db, companyData, 'irregularPayments'), irregularPayload);
      const newId = ref.id;
      await syncLedgerFromSource(db, companyData, {
        sourceType: LEDGER_SOURCE_TYPES.labour,
        sourceCollection: 'irregularPayments',
        sourceId: newId,
        doc: { ...irregularPayload, id: newId },
      });

      if (recordInEmployeeCashFlowLabour && selectedEmployeeForCashFlowLabour) {
        const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlowLabour);
        const paymentModeRaw = (irregularForm.paymentMode || 'Cash').toLowerCase();
        const paymentMode = paymentModeRaw === 'upi' ? 'upi' : 'cash';
        const transactionData = {
          employeeId: selectedEmployeeForCashFlowLabour,
          employeeName: selectedEmployee?.name || '',
          transactionType: 'outflow',
          amount: amountNum,
          paymentMode,
          category: 'Labour/Advance',
          description: `${irregularForm.paymentType}: ${personName}${irregularForm.remark ? ` - ${irregularForm.remark}` : ''}`,
          linkedPaymentId: newId,
          linkedPaymentType: 'labour',
          date: irregularForm.date || new Date().toISOString().split('T')[0], // string date so report date-range filter includes this transaction
        };
        const cashResult = await EmployeeCashManager.createCashTransaction(companyData, transactionData);
        if (!cashResult.success) {
          warning(`Payment saved but employee cash flow record failed: ${cashResult.error}`, { title: 'Cash flow', duration: 6000 });
        } else {
          if (markLabourSettledFromWallet && selectedEmployee) {
            await updateDoc(doc(getCollectionForCompany(db, companyData, 'irregularPayments'), newId), {
              settled: true,
              appliedInMonth: new Date().toISOString().slice(0, 7),
              settlementRemark: 'Settled via employee wallet',
              settledAt: serverTimestamp(),
              settledFromOtherWallet: {
                amount: amountNum,
                sourceEmployeeId: selectedEmployeeForCashFlowLabour,
                sourceEmployeeName: selectedEmployee.name,
              },
            });
          }
        }
      }

      setIrregularForm({
        date: new Date().toISOString().split('T')[0],
        personType: 'Employee',
        employeeId: '',
        personName: '',
        paymentType: 'Advance',
        amount: '',
        remark: '',
        paymentMode: 'Cash',
        settlementRemark: '',
        workOrderId: '',
        phaseId: '',
        costCategory: '',
      });
      setRecordInEmployeeCashFlowLabour(false);
      setSelectedEmployeeForCashFlowLabour('');
      setMarkLabourSettledFromWallet(false);

      const details = [
        `${personName} • ${irregularForm.paymentMode}`,
        ...(recordInEmployeeCashFlowLabour && selectedEmployeeForCashFlowLabour
          ? ['Recorded in employee cash flow' + (markLabourSettledFromWallet ? ' • Marked as settled from wallet' : '')]
          : []),
      ].filter(Boolean);
      paymentSuccess(
        `${irregularForm.paymentType} of ${formatCurrency(amountNum, companyCurrency)} added successfully!`,
        {
          title: 'Payment Added Successfully',
          details: details.join(' • '),
          duration: 6000
        }
      );
    } catch (e) {
      console.error('Failed to save irregular payment', e);
      error('Failed to save payment. Please try again.');
    }
  };
  const handleDeleteIrregular = async (id) => {
    if (!window.confirm('Delete this entry? Any linked employee wallet transaction will be reversed.')) return;
    try {
      const companyData = { companyId, appId, userId };
      const cascadeResult = await EmployeeCashManager.deleteTransactionsForIrregularPayment(companyData, id);
      if (!cascadeResult.success && cascadeResult.error) {
        error(`Could not delete linked wallet transactions: ${cascadeResult.error}. Delete them from Employee Cash Flow Report first, then delete this entry.`, { title: 'Cascade Delete Failed', duration: 8000 });
        return;
      }
      await removeLedgerEntry(db, companyData, {
        sourceType: LEDGER_SOURCE_TYPES.labour,
        sourceId: id,
      });
      await deleteDoc(doc(getCollectionForCompany(db, companyData, 'irregularPayments'), id));
      const msg = cascadeResult.deletedCount > 0
        ? `Payment entry and ${cascadeResult.deletedCount} linked wallet transaction(s) deleted. Amounts reversed.`
        : 'Payment entry deleted successfully!';
      success(msg, { title: 'Entry Deleted' });
    } catch (e) {
      console.error('Delete irregular failed', e);
      error('Failed to delete payment entry. Please try again.');
    }
  };

  const handleOpenSettleExpenseFromWallet = (expense) => {
    setSettleExpenseModal(expense);
    setSettleExpenseDate(new Date().toISOString().split('T')[0]);
    setSettleExpenseEmployeeId('');
    const pm = (expense.paymentMode || 'Cash').toLowerCase();
    setSettleExpensePaymentMode(pm === 'upi' ? 'upi' : 'cash');
    setSettleExpenseRemark('');
    setSettleExpenseEmployeeBalance(null);
  };

  const handleConfirmSettleExpenseFromWallet = async (e) => {
    e.preventDefault();
    if (!settleExpenseModal || !settleExpenseEmployeeId) {
      warning('Select an employee whose wallet to use.');
      return;
    }
    const companyData = { companyId, appId, userId };
    const amountNum = Number(settleExpenseModal.amount) || 0;
    const paymentMode = settleExpensePaymentMode || 'cash';
    const balance = settleExpenseEmployeeBalance || { cash: 0, upi: 0, total: 0 };
    const available = paymentMode === 'cash' ? balance.cash : balance.upi;
    if (available < amountNum) {
      warning(`Insufficient wallet balance. Available: ₹${(available || 0).toLocaleString('en-IN')}`, {
        title: 'Employee wallet',
        duration: 5000,
      });
      return;
    }
    const selectedEmployee = employees?.find((emp) => emp.id === settleExpenseEmployeeId);
    if (!selectedEmployee || !isEmployeeActive(selectedEmployee)) {
      warning('Selected employee is inactive or removed. Choose an active employee wallet.', {
        title: 'Inactive Employee',
        duration: 5000,
      });
      return;
    }
    const personLabel = settleExpenseModal.personName || '—';
    const categoryLabel =
      settleExpenseModal.group === 'variable' ? 'Variable Expense' : 'Fixed Expense';
    const entryLabel = [settleExpenseModal.head, settleExpenseModal.constantEntry].filter(Boolean).join(' - ');
    setSettleExpenseSubmitting(true);
    try {
      const transactionData = {
        employeeId: settleExpenseEmployeeId,
        employeeName: selectedEmployee?.name || '',
        transactionType: 'outflow',
        amount: amountNum,
        paymentMode,
        category: categoryLabel,
        description: `Settle: ${entryLabel || categoryLabel} - ${personLabel}${
          settleExpenseRemark ? ` (${settleExpenseRemark})` : ''
        }`,
        linkedPaymentId: settleExpenseModal.id,
        linkedPaymentType: 'expense',
        date: settleExpenseDate || new Date().toISOString().split('T')[0],
      };
      const cashResult = await EmployeeCashManager.createCashTransaction(companyData, transactionData);
      if (!cashResult.success) {
        warning(`Settlement failed: ${cashResult.error}`, { title: 'Cash flow', duration: 6000 });
        setSettleExpenseSubmitting(false);
        return;
      }
      const appliedMonth = (settleExpenseDate || '').slice(0, 7);
      await updateDoc(doc(getCollectionForCompany(db, companyData, 'expenses'), settleExpenseModal.id), {
        settled: true,
        appliedInMonth: appliedMonth,
        settlementRemark: settleExpenseRemark
          ? `Settled via employee wallet. ${settleExpenseRemark}`
          : 'Settled via employee wallet',
        settledAt: serverTimestamp(),
        settledFromOtherWallet: {
          amount: amountNum,
          sourceEmployeeId: settleExpenseEmployeeId,
          sourceEmployeeName: selectedEmployee?.name || '',
        },
      });
      setSettleExpenseModal(null);
      await reloadExpenses();
      success(
        `Expense of ${formatCurrency(amountNum, companyCurrency)} settled from ${selectedEmployee?.name}'s wallet.`,
        { title: 'Settled from wallet', duration: 5000 }
      );
    } catch (err) {
      console.error('Settle expense from wallet failed', err);
      error(err?.message || 'Failed to settle from wallet. Please try again.');
    } finally {
      setSettleExpenseSubmitting(false);
    }
  };

  const handleOpenSettleLabourFromWallet = (payment) => {
    setSettleLabourModalPayment(payment);
    setSettleLabourDate(new Date().toISOString().split('T')[0]);
    setSettleLabourEmployeeId('');
    const pm = (payment.paymentMode || 'Cash').toLowerCase();
    setSettleLabourPaymentMode(pm === 'upi' ? 'upi' : 'cash');
    setSettleLabourRemark('');
    setSettleLabourEmployeeBalance(null);
  };

  const handleConfirmSettleLabourFromWallet = async (e) => {
    e.preventDefault();
    if (!settleLabourModalPayment || !settleLabourEmployeeId) {
      warning('Select an employee whose wallet to use.');
      return;
    }
    const companyData = { companyId, appId, userId };
    const amountNum = Number(settleLabourModalPayment.amount) || 0;
    const paymentMode = settleLabourPaymentMode || 'cash';
    const balance = settleLabourEmployeeBalance || { cash: 0, upi: 0, total: 0 };
    const available = paymentMode === 'cash' ? balance.cash : balance.upi;
    if (available < amountNum) {
      warning(`Insufficient wallet balance. Available: ₹${(available || 0).toLocaleString('en-IN')}`, { title: 'Employee wallet', duration: 5000 });
      return;
    }
    const selectedEmployee = employees?.find((emp) => emp.id === settleLabourEmployeeId);
    if (!selectedEmployee || !isEmployeeActive(selectedEmployee)) {
      warning('Selected employee is inactive or removed. Choose an active employee wallet.', {
        title: 'Inactive Employee',
        duration: 5000,
      });
      return;
    }
    const personName = settleLabourModalPayment.personType === 'Employee'
      ? (employees.find(emp => emp.employeeId === settleLabourModalPayment.employeeId)?.name || 'Employee')
      : (settleLabourModalPayment.personName || '-');
    setSettleLabourSubmitting(true);
    try {
      const transactionData = {
        employeeId: settleLabourEmployeeId,
        employeeName: selectedEmployee?.name || '',
        transactionType: 'outflow',
        amount: amountNum,
        paymentMode,
        category: 'Labour/Advance',
        description: `Settle: ${settleLabourModalPayment.paymentType} - ${personName}${settleLabourRemark ? ` (${settleLabourRemark})` : ''}`,
        linkedPaymentId: settleLabourModalPayment.id,
        linkedPaymentType: 'labour',
        date: settleLabourDate || new Date().toISOString().split('T')[0],
      };
      const cashResult = await EmployeeCashManager.createCashTransaction(companyData, transactionData);
      if (!cashResult.success) {
        warning(`Settlement failed: ${cashResult.error}`, { title: 'Cash flow', duration: 6000 });
        setSettleLabourSubmitting(false);
        return;
      }
      const appliedMonth = (settleLabourDate || '').slice(0, 7);
      await updateDoc(doc(getCollectionForCompany(db, companyData, 'irregularPayments'), settleLabourModalPayment.id), {
        settled: true,
        appliedInMonth: appliedMonth,
        settlementRemark: settleLabourRemark ? `Settled via employee wallet. ${settleLabourRemark}` : 'Settled via employee wallet',
        settledAt: serverTimestamp(),
        settledFromOtherWallet: {
          amount: amountNum,
          sourceEmployeeId: settleLabourEmployeeId,
          sourceEmployeeName: selectedEmployee?.name || '',
        },
      });
      setSettleLabourModalPayment(null);
      success(`Labour payment of ${formatCurrency(amountNum, companyCurrency)} settled from ${selectedEmployee?.name}'s wallet.`, { title: 'Settled from wallet', duration: 5000 });
    } catch (err) {
      console.error('Settle labour from wallet failed', err);
      error(err?.message || 'Failed to settle from wallet. Please try again.');
    } finally {
      setSettleLabourSubmitting(false);
    }
  };

  const handleDeleteSalaryPayment = async (paymentId) => {
    // Check if the payment ID exists in the current data
    const paymentExists = salaryPayments.find(p => p.id === paymentId);
    if (!paymentExists) {
      console.error('Payment not found in current data:', paymentId);
      error('Payment not found. Please refresh the page and try again.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this salary payment? This will also remove any related wallet transactions (from employee balance or other employee wallet). This action cannot be undone.')) {
      try {
        const companyData = { companyId, appId, userId };
        const hasWalletSettlement = (paymentExists.settledFromOtherWallet?.amount > 0);
        const cascadeResult = await EmployeeCashManager.deleteTransactionsForSalary(companyData, paymentId);

        if (!cascadeResult.success && cascadeResult.error) {
          error(`Could not delete linked wallet transactions: ${cascadeResult.error}. Salary NOT deleted. Delete wallet transactions from Employee Cash Flow Report first, then delete this salary.`, { title: 'Cascade Delete Failed', duration: 8000 });
          return;
        }
        if (hasWalletSettlement && cascadeResult.deletedCount === 0) {
          const proceed = window.confirm('No linked wallet transactions were found. Amounts will NOT be reversed. Delete transactions from Employee Cash Flow Report to reverse balances. Proceed with deleting salary entry anyway?');
          if (!proceed) return;
        } else if (cascadeResult.deletedCount > 0 && !cascadeResult.success) {
          warning(`Deleted ${cascadeResult.deletedCount} related transactions but some failed: ${cascadeResult.error}`);
        }

        await deleteDoc(doc(getCollectionForCompany(db, companyData, 'salaryPayments'), paymentId));
        const msg = cascadeResult.deletedCount > 0
          ? `Salary payment and ${cascadeResult.deletedCount} related wallet transaction(s) deleted. Amounts reversed.`
          : 'Salary payment deleted successfully!';
        success(msg, { title: 'Payment Deleted' });
        await reloadSalaryPayments();
      } catch (err) {
        console.error('Error deleting salary payment:', err);
        error(`Error deleting salary payment: ${err.message}. Please try again.`);
      }
    }
  };



  const handleSaveEditSalaryPayment = async () => {
    try {
      const fixedTotal = fixedSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
      const performanceTotal = performanceSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
      const totalEarnings = fixedTotal + performanceTotal;
      const totalDeductions = salaryDeductions.reduce((sum, deduction) => sum + (Number(deduction.amount) || 0), 0);
      const netAmount = totalEarnings - totalDeductions;

      await updateDoc(doc(getCollectionForCompany(db, { companyId, appId, userId }, 'salaryPayments'), editingSalaryPayment.id), {
        fixedRows: fixedSalaryRows,
        performanceRows: performanceSalaryRows,
        deductions: salaryDeductions,
        totalEarnings,
        totalDeductions,
        netAmount,
        paymentMode: salaryPaymentMode,
        updatedAt: serverTimestamp(),
      });
      await reloadSalaryPayments();

      paymentSuccess(
        'Salary payment updated successfully!',
        {
          title: 'Payment Updated',
          duration: 5000
        }
      );
      setShowEditSalaryModal(false);
      setEditingSalaryPayment(null);
      setIsEditingSalary(false); // Reset editing flag
      setFixedSalaryRows([]);
      setPerformanceSalaryRows([]);
      setSalaryDeductions([]);
      // Clear any pre-filled selection in main form
      setSalaryEmployeeId('');
      setSelectedEmployeeDisplay('');
      setSalaryDate('');
      setSalaryMonth('');
    } catch (error) {
      console.error('Error updating salary payment:', error);
      error('Error updating salary payment. Please try again.');
    }
  };

  const handleCancelEditSalaryPayment = () => {
    setShowEditSalaryModal(false);
    setEditingSalaryPayment(null);
    setIsEditingSalary(false); // Reset editing flag
    setFixedSalaryRows([]);
    setPerformanceSalaryRows([]);
    setSalaryDeductions([]);
    // Also reset main form fields so edited values don't linger
    setSalaryEmployeeId('');
    setSelectedEmployeeDisplay('');
    setSalaryDate('');
    setSalaryMonth('');
  };


  // Test function for debugging employee data
  const testEmployeeData = () => {
    console.log('=== Employee Data Test ===');
    console.log('Total employees:', employees.length);
    if (employees.length > 0) {
      const firstEmployee = employees[0];
      console.log('First employee data:', firstEmployee);
      console.log('Basic Salary Amount:', firstEmployee.basicSalaryAmount);
      console.log('HRA Amount:', firstEmployee.hraAmount);
      console.log('Conveyance Amount:', firstEmployee.conveyanceAmount);
      console.log('Employer PF Amount:', firstEmployee.employerPFAmount);
      console.log('Gratuity Amount:', firstEmployee.gratuityAmount);
      console.log('Special Allowance Amount:', firstEmployee.specialAllowanceAmount);
      console.log('Total CTC:', firstEmployee.totalCTC);
    }
    console.log('========================');
  };

  // Test function for debugging salary delete functionality
  const testSalaryDelete = () => {
    console.log('=== Salary Delete Test ===');
    console.log('Salary Payments:', salaryPayments);
    console.log('=======================');
  };

  // Make test functions available globally
  useEffect(() => {
    window.testEmployeeData = testEmployeeData;
    window.testSalaryDelete = testSalaryDelete;
  }, [employees, salaryPayments]);

  // Salary slip functionality
  const [showSalarySlip, setShowSalarySlip] = useState(false);
  const [selectedSalaryPayment, setSelectedSalaryPayment] = useState(null);

  // Collapsible cards state
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [expandedSalaryCards, setExpandedSalaryCards] = useState(new Set());

  // ESC key handler for edit modals
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        console.log('ESC key pressed');
        if (showSalarySlip) {
          handleCloseSalarySlip();
        } else if (showEditSalaryModal) {
          handleCancelEditSalaryPayment();
        } else if (editingExpenseId) {
          handleCancelEditExpense();
        } else if (showEditForm) {
          setShowEditForm(false);
        } else if (showEmployeeDropdown) {
          setShowEmployeeDropdown(false);
        }
      }
    };

    if (showSalarySlip || showEditSalaryModal || editingExpenseId || showEditForm || showEmployeeDropdown) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [showSalarySlip, showEditSalaryModal, editingExpenseId, showEditForm, showEmployeeDropdown]);

  // Card expansion handlers
  const toggleCard = (cardId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const isCardExpanded = (cardId) => expandedCards.has(cardId);

  // Salary slip functions
  const handleViewSalarySlip = (salaryPayment) => {
    setSelectedSalaryPayment(salaryPayment);
    setShowSalarySlip(true);
  };

  const handleCloseSalarySlip = () => {
    setShowSalarySlip(false);
    setSelectedSalaryPayment(null);
  };

  const handlePrintSalarySlip = async () => {
    try {
      // Get the salary slip content element
      const element = document.getElementById('salary-slip-content');
      if (!element) {
        error('Salary slip content not found', { title: 'Print Error' });
        return;
      }

      // Capture the element as canvas
      const canvas = await html2canvas(element, {
        scale: 2, // High quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/png');
      
      // Create print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Salary Slip - ${selectedSalaryPayment?.employeeName || 'Employee'}</title>
            <style>
              body { 
                margin: 0; 
                padding: 20px; 
                background: white;
              }
              img { 
                width: 100%; 
                height: auto; 
                max-width: 100%;
              }
              @media print {
                body { margin: 0; padding: 0; }
                img { width: 100%; height: auto; }
              }
            </style>
          </head>
          <body>
            <img src="${imgData}" alt="Salary Slip" />
          </body>
        </html>
      `);
      printWindow.document.close();
      
      // Wait for image to load then print
      printWindow.onload = () => {
        printWindow.print();
        
        // Close the window after printing
        printWindow.onafterprint = () => {
          printWindow.close();
        };
        
        // Enhanced auto-close logic
        let isPrintDialogOpen = true;
        let closeAttempts = 0;
        const maxCloseAttempts = 10;
        
        // Check if print dialog is closed and close the window
        const checkAndClose = () => {
          if (printWindow.closed) return;
          
          // Try to detect if print dialog is still open
          try {
            // If we can access the window and it's not closed, check if print dialog is open
            if (printWindow.document && !printWindow.document.hidden) {
              // Print dialog might still be open, check again
              if (isPrintDialogOpen && closeAttempts < maxCloseAttempts) {
                closeAttempts++;
                setTimeout(checkAndClose, 1000);
                return;
              }
            }
          } catch (e) {
            // Window might be closed or inaccessible
          }
          
          // Close the window
          if (!printWindow.closed) {
            printWindow.close();
          }
        };
        
        // Listen for print dialog events
        printWindow.addEventListener('beforeprint', () => {
          isPrintDialogOpen = true;
        });
        
        printWindow.addEventListener('afterprint', () => {
          isPrintDialogOpen = false;
          setTimeout(() => {
            if (!printWindow.closed) {
              printWindow.close();
            }
          }, 500);
        });
        
        // Also listen for beforeunload (when user cancels)
        printWindow.addEventListener('beforeunload', () => {
          isPrintDialogOpen = false;
          setTimeout(() => {
            if (!printWindow.closed) {
              printWindow.close();
            }
          }, 100);
        });
        
        // Start checking after print dialog appears
        setTimeout(checkAndClose, 2000);
        
        // Fallback: Force close after 15 seconds
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.close();
          }
        }, 15000);
      };
    } catch (error) {
      console.error('Error generating print:', error);
      error('Error generating print. Please try again.', { title: 'Print Error' });
    }
  };

  const handlePDFSalarySlip = async () => {
    try {
      // Get the salary slip content element
      const element = document.getElementById('salary-slip-content');
      if (!element) {
        error('Salary slip content not found', { title: 'Print Error' });
        return;
      }

      // Capture the element as canvas
      const canvas = await html2canvas(element, {
        scale: 2, // High quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      
      // Get page dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Calculate image dimensions to fit page and support multipage
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let remainingHeight = imgHeight;
      let positionY = margin;

      // If the rendered height exceeds one page, add slices across pages
      if (imgHeight <= pageHeight - margin * 2) {
        doc.addImage(imgData, 'PNG', margin, positionY, imgWidth, imgHeight);
      } else {
        // Draw in chunks: we simulate vertical offset by reusing the same image and moving Y
        // jsPDF does not support image cropping directly, but scaling with page breaks works well visually for slip-style content
        let pageIndex = 0;
        while (remainingHeight > 0 && pageIndex < 50) {
          const availableHeight = pageHeight - margin * 2;
          const drawHeight = Math.min(availableHeight, remainingHeight);
          doc.addImage(imgData, 'PNG', margin, margin, imgWidth, drawHeight);
          remainingHeight -= drawHeight;
          if (remainingHeight > 0) {
            doc.addPage();
            pageIndex += 1;
          }
        }
      }
      
      // Save PDF
      doc.save(`salary-slip-${selectedSalaryPayment?.employeeName || 'employee'}-${selectedSalaryPayment?.month || 'unknown'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      error('Error generating PDF. Please try again.', { title: 'PDF Generation Error' });
    }
  };

  // Authentication check after all hooks are declared
  // Dev mode: allow rendering even if auth mocked
  if (!isAuthReady) {
    // continue rendering with mock data instead of blocking UI
  }
  
  if (!userId) {
    return <div className="max-w-5xl mx-auto p-6 bg-white rounded shadow mt-8">
      <h2 className="text-lg font-semibold mb-4">Expenses</h2>
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">No user ID found. Please log in again.</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Reload Page
        </button>
      </div>
    </div>;
  }
  
  if (!appId) {
    return <div className="max-w-5xl mx-auto p-6 bg-white rounded shadow mt-8">
      <h2 className="text-lg font-semibold mb-4">Expenses</h2>
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">No app ID found. Please check configuration.</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 max-w-full overflow-x-hidden">
      <div className="w-full px-2 sm:px-4 lg:px-6">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-800">Expenses Management</h1>
              <p className="text-sm text-gray-600 mt-1 hidden sm:block">Manage your company expenses efficiently</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="text-sm text-gray-500">
                Total:{' '}
                <span className={`font-semibold tabular-nums ${getGroupTabAccent(selectedGroup).stat}`}>
                  {formatCurrency(totalAmount, companyCurrency)}
                </span>
              </div>
              <div className="text-sm text-gray-500">
                Entries:{' '}
                <span className={`font-semibold tabular-nums ${getGroupTabAccent(selectedGroup).stat}`}>
                  {totalCount}
                </span>
              </div>
            </div>
          </div>
        </div>
      

      
      {/* Summary/Analytics - Removed as per user request */}
      {/* Tabs for groups - Desktop and Mobile */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="hidden md:inline-flex w-full max-w-3xl p-1.5 bg-slate-100/90 border border-slate-200 rounded-xl shadow-sm">
            {GROUPS.map((group) => {
              const accent = getGroupTabAccent(group.key);
              const isActive = selectedGroup === group.key;
              return (
                <button
                  key={group.key}
                  type="button"
                  onClick={() => {
                    setSelectedGroup(group.key);
                    setPage(1);
                  }}
                  className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${accent.focusRing} ${
                    isActive ? accent.active : 'text-gray-600 hover:text-gray-900 hover:bg-white/90'
                  }`}
                >
                  {group.label}
                </button>
              );
            })}
          </div>

          <div className="md:hidden">
            <MobileScrollTabs
              tabs={GROUPS.map((group) => ({ id: group.key, label: group.label }))}
              activeId={selectedGroup}
              onChange={(key) => {
                setSelectedGroup(key);
                setPage(1);
              }}
              ariaLabel="Expense section"
              navClassName="border border-slate-200 bg-white shadow-sm"
              getButtonClassName={(_, isActive) =>
                isActive
                  ? 'bg-slate-800 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/90'
              }
            />
          </div>
        </div>

        {selectedGroup !== 'salaries' && (
          <div className="relative shrink-0 self-end sm:self-center" ref={exportRef}>
            <button
              type="button"
              onClick={() => setExportDropdownOpen((o) => !o)}
              className={`inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors ${getGroupTabAccent(selectedGroup).exportHover}`}
            >
              <Download className="h-4 w-4 opacity-70" aria-hidden />
              <span className="hidden sm:inline">Export</span>
              <ChevronDown
                className={`h-4 w-4 opacity-60 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
            {exportDropdownOpen && (
              <div className="absolute right-0 mt-2 w-52 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden ring-1 ring-black/5">
                <button
                  type="button"
                  onClick={handleExportCSV}
                  className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50"
                >
                  Export as CSV
                </button>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50"
                >
                  Export as Excel
                </button>
                <button
                  type="button"
                  onClick={handleExportPDF}
                  className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50"
                >
                  Export as PDF
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Group-specific form */}
      {renderGroupForm()}



      {/* Salary Payment Edit Modal */}
      {showEditSalaryModal && editingSalaryPayment && (
        <StandardModal
          section={MODAL_SECTIONS.SALARY}
          isOpen
          onClose={handleCancelEditSalaryPayment}
          title="Edit salary payment"
          size="4xl"
          zIndex="z-[100]"
        >
            {/* Employee Info */}
            <div className="mb-4 rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/80 to-white p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Employee:</span> {editingSalaryPayment.employeeName}
                </div>
                <div>
                  <span className="font-medium">Date:</span> {editingSalaryPayment.date}
                </div>
                <div>
                  <span className="font-medium">Month:</span> {editingSalaryPayment.month}
                </div>
                <div>
                  <span className="font-medium">Post:</span> {editingSalaryPayment.post}
                </div>
                <div>
                  <span className="font-medium">Payment Mode:</span>
                  <AppSelect
                    value={salaryPaymentMode}
                    onChange={(e) => setSalaryPaymentMode(e.target.value)}
                    aria-label="Payment mode"
                    sheetTitle="Payment mode"
                    options={[
                      { value: 'Cash', label: 'Cash' },
                      { value: 'UPI', label: 'UPI' },
                      { value: 'Bank Transfer', label: 'Bank Transfer' },
                      { value: 'Cheque', label: 'Cheque' },
                      { value: 'Other', label: 'Other' },
                    ]}
                    triggerClassName={`${APP_SELECT_FILTER_TRIGGER} mt-1 sm:mt-0 sm:ml-2 rounded-lg`}
                  />
                </div>
              </div>
            </div>

            {/* Fixed Salary Components - editable when editing existing salary */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h6 className="text-sm font-semibold text-gray-700">Fixed Salary Components</h6>
                <button
                  type="button"
                  onClick={addFixedSalaryRow}
                  className={SALARY_THEME.btnAddLine}
                >
                  + Add line
                </button>
              </div>
              <div className="space-y-2">
                {fixedSalaryRows.map((row, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:flex-wrap gap-2 items-stretch sm:items-center bg-white p-3 rounded border">
                    <input
                      type="text"
                      value={row.type || ''}
                      onChange={e => handleFixedSalaryRowChange(idx, 'type', e.target.value)}
                      placeholder="Component name"
                      className="border rounded py-2.5 px-3 text-base sm:text-sm w-full sm:min-w-[120px] sm:flex-1"
                    />
                    <input
                      type="number"
                      value={row.amount}
                      onChange={e => handleFixedSalaryRowChange(idx, 'amount', e.target.value)}
                      placeholder="Amount"
                      className="border rounded py-2.5 px-3 text-base sm:text-sm w-full sm:w-28"
                      min="0"
                      step="0.01"
                    />
                    <input
                      value={row.remark || ''}
                      onChange={e => handleFixedSalaryRowChange(idx, 'remark', e.target.value)}
                      placeholder="Remark"
                      className="border rounded py-2.5 px-3 text-base sm:text-sm w-full sm:w-32"
                    />
                    {row.removable ? (
                      <button
                        type="button"
                        onClick={() => removeFixedSalaryRow(idx)}
                        className="text-red-600 hover:text-red-800 text-sm px-3 py-2.5 min-h-[44px] touch-manipulation self-end sm:self-center"
                        title="Remove component"
                      >
                        ✕
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 px-2">Fixed</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Performance-Based Components */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h6 className="text-sm font-semibold text-gray-700">Performance-Based Components</h6>
                <button
                  type="button"
                  onClick={addPerformanceRow}
                  className={SALARY_THEME.btnAddLine}
                >
                  + Add line
                </button>
              </div>
              <div className="space-y-2">
                {performanceSalaryRows.map((row, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:flex-wrap gap-2 items-stretch sm:items-center bg-white p-3 rounded border">
                    <AppSelect
                      value={row.type}
                      onChange={e => handlePerformanceRowChange(idx, 'type', e.target.value)}
                      placeholder="Select Type"
                      aria-label="Performance type"
                      sheetTitle="Performance type"
                      options={[
                        { value: '', label: 'Select Type' },
                        { value: 'Advance', label: 'Advance' },
                        { value: 'Incentive', label: 'Incentive' },
                        { value: 'Bonus', label: 'Bonus' },
                        { value: 'Other', label: 'Other' },
                      ]}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded py-2.5 text-base sm:text-sm w-full sm:min-w-[120px]`}
                    />
                    {row.type === 'Other' && (
                      <input
                        type="text"
                        value={row.otherType || ''}
                        onChange={e => handlePerformanceRowChange(idx, 'otherType', e.target.value)}
                        placeholder="Specify type"
                        className="border rounded p-1 text-sm w-32"
                        required
                      />
                    )}
                    <input
                      type="number"
                      value={row.amount}
                      onChange={e => handlePerformanceRowChange(idx, 'amount', e.target.value)}
                      placeholder="Amount"
                      className="border rounded py-2.5 px-3 text-base sm:text-sm w-full sm:w-28"
                      required
                    />
                    <input
                      value={row.remark}
                      onChange={e => handlePerformanceRowChange(idx, 'remark', e.target.value)}
                      placeholder="Remark"
                      className="border rounded py-2.5 px-3 text-base sm:text-sm w-full sm:w-32"
                    />
                    <button
                      type="button"
                      onClick={() => removePerformanceRow(idx)}
                      className="text-red-600 hover:text-red-800 text-sm px-3 py-2.5 min-h-[44px] touch-manipulation self-end sm:self-center"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Deductions */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h6 className="text-sm font-semibold text-gray-700">Deductions</h6>
                <button
                  type="button"
                  onClick={addDeduction}
                  className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                >
                  + Add Deduction
                </button>
              </div>
              <div className="space-y-2">
                {salaryDeductions.map((deduction, idx) => (
                  <div key={idx} className="flex gap-2 items-center bg-white p-2 rounded border">
                    <AppSelect
                      value={deduction.reason}
                      onChange={e => handleDeductionChange(idx, 'reason', e.target.value)}
                      placeholder="Select Reason"
                      aria-label="Deduction reason"
                      sheetTitle="Deduction reason"
                      options={[
                        { value: '', label: 'Select Reason' },
                        { value: 'Advance Repayment', label: 'Advance Repayment' },
                        { value: 'Unpaid Leave', label: 'Unpaid Leave' },
                        { value: 'Tax Deduction', label: 'Tax Deduction' },
                        { value: 'Other', label: 'Other' },
                      ]}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded p-1 text-sm min-w-[120px]`}
                    />
                    {deduction.reason === 'Other' && (
                      <input
                        type="text"
                        value={deduction.otherReason || ''}
                        onChange={e => handleDeductionChange(idx, 'otherReason', e.target.value)}
                        placeholder="Specify reason"
                        className="border rounded p-1 text-sm w-32"
                        required
                      />
                    )}
                    <input
                      type="number"
                      value={deduction.amount}
                      onChange={e => handleDeductionChange(idx, 'amount', e.target.value)}
                      placeholder="Amount"
                      className="border rounded p-1 text-sm w-24"
                      required
                    />
                    <input
                      value={deduction.remark}
                      onChange={e => handleDeductionChange(idx, 'remark', e.target.value)}
                      placeholder="Remark"
                      className="border rounded p-1 text-sm w-32"
                    />
                    <button
                      type="button"
                      onClick={() => removeDeduction(idx)}
                      className="text-red-600 hover:text-red-800 text-sm px-2 py-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Settled Advances (for reference) */}
            <div className="mb-4">
              <h6 className="text-sm font-semibold mb-2 text-gray-700">Settled Advances (Reference Only)</h6>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {irregularPayments
                  .filter(p => p.settled && p.personType === 'Employee' && p.employeeId === editingSalaryPayment.employeeId)
                  .map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center rounded-lg border border-sky-100 bg-sky-50/60 p-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-sky-900">{item.paymentType}</div>
                        <div className="text-xs text-sky-700">Settled in: {item.appliedInMonth || '-'}</div>
                        <div className="text-xs text-gray-500">{item.settlementRemark || 'No remark'}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-sky-800 tabular-nums">₹{Number(item.amount).toLocaleString('en-IN', {maximumFractionDigits: 2})}</div>
                        <div className="text-xs text-sky-600">✓ Settled</div>
                      </div>
                    </div>
                  ))}
                {irregularPayments.filter(p => p.settled && p.personType === 'Employee' && p.employeeId === editingSalaryPayment.employeeId).length === 0 && (
                  <div className="text-center py-2 text-gray-400 text-sm">No settled advances</div>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
              <h6 className="text-sm font-semibold mb-2 text-gray-700">Payment summary</h6>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-sky-700">Total earnings</span>
                  <div className={`text-lg font-bold tabular-nums ${SALARY_THEME.earningsText}`}>
                    ₹{(fixedSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0) + 
                       performanceSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)).toLocaleString('en-IN')}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-red-600">Total Deductions:</span>
                  <div className="text-lg font-bold text-red-700">
                    ₹{salaryDeductions.reduce((sum, deduction) => sum + (Number(deduction.amount) || 0), 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-blue-600">Net Amount:</span>
                  <div className="text-lg font-bold text-blue-700">
                    ₹{((fixedSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0) + 
                        performanceSalaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)) - 
                       salaryDeductions.reduce((sum, deduction) => sum + (Number(deduction.amount) || 0), 0)).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2 border-t border-slate-100">
              <StandardButton
                variant="secondary"
                type="button"
                onClick={handleCancelEditSalaryPayment}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
              >
                Cancel
              </StandardButton>
              <StandardButton
                type="button"
                onClick={handleSaveEditSalaryPayment}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
              >
                Save changes
              </StandardButton>
            </div>
        </StandardModal>
      )}

      {/* Salary Slip Modal */}
      {showSalarySlip && selectedSalaryPayment && (
        <SalarySlip
          salaryPayment={selectedSalaryPayment}
          currentCompany={currentCompany}
          onClose={handleCloseSalarySlip}
          onPrint={handlePrintSalarySlip}
          onPDF={handlePDFSalarySlip}
        />
      )}
      </div>
    </div>
  );
};

export default Expenses; 
